import { useState, useMemo, useRef, useEffect, forwardRef } from 'react';
import { COLORS, FONTS, TYPE, SPACE, CARD, FORM } from './styles/theme.js';
import { COMPANY_SIZES, CMS_DATA, INDUSTRIES, SALARY_DATA, getFAQs, AOR_TIERS, getVariantBySlug } from './data/index.js';
import { 
  formatCurrency, 
  calculateProjectCosts,
  initTracking,
  trackCalculatorInteraction,
  trackUrlAnalyze,
  trackCtaClick,
  trackFormStart,
  trackFormSubmit,
  trackFormSubmitSuccess,
  trackFormSubmitError,
  trackGenerateLead,
  markCalculatorInteraction,
  getAttribution,
} from './utils/index.js';
import Nav from './components/Nav.jsx';

// Configuration - Update these for your agency
const AGENCY_CONFIG = {
  name: 'Azai Studios',
  calendarUrl: 'https://calendar.app.google/AiytCRBqgXNzkWG78',
};

// Reusable Components
function SectionHeader({ title, step }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: SPACE.sm,
      marginBottom: SPACE.lg,
    }}>
      <span style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: COLORS.orange,
        color: COLORS.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: TYPE.label.size,
        fontWeight: 600,
        fontFamily: FONTS.body,
        flexShrink: 0,
      }}>
        {step}
      </span>
      <h2 style={{
        fontSize: TYPE.body.size,
        fontWeight: 600,
        color: COLORS.black,
        fontFamily: FONTS.body,
        margin: 0,
        lineHeight: TYPE.body.lineHeight,
      }}>
        {title}
      </h2>
    </div>
  );
}

function Slider({ label, value, onChange, min, max, step = 1, suffix = '' }) {
  const percent = ((value - min) / (max - min)) * 100;

  return (
    <div style={{ marginBottom: FORM.fieldGap }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE.sm, alignItems: 'baseline' }}>
        <label style={{
          fontSize: TYPE.label.size,
          color: COLORS.gray,
          fontWeight: 400,
          fontFamily: FONTS.body,
          lineHeight: TYPE.label.lineHeight,
        }}>
          {label}
        </label>
        <span style={{
          fontSize: TYPE.body.size,
          color: COLORS.black,
          fontWeight: 600,
          fontFamily: FONTS.body,
        }}>
          {value}{suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: '100%',
          height: 6,
          borderRadius: 3,
          background: `linear-gradient(to right, ${COLORS.orange} 0%, ${COLORS.orange} ${percent}%, ${COLORS.lightGray} ${percent}%, ${COLORS.lightGray} 100%)`,
          appearance: 'none',
          cursor: 'pointer',
        }}
      />
    </div>
  );
}

function SelectDropdown({ label, value, onChange, options, recommendedKey }) {
  return (
    <div style={{ marginBottom: FORM.fieldGap }}>
      <label style={{
        fontSize: TYPE.label.size,
        color: COLORS.gray,
        fontWeight: 400,
        display: 'block',
        marginBottom: FORM.labelMargin,
        fontFamily: FONTS.body,
        lineHeight: TYPE.label.lineHeight,
      }}>
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: FORM.inputPadding,
          borderRadius: FORM.inputRadius,
          border: `1px solid ${COLORS.lightGray}`,
          background: COLORS.white,
          fontSize: TYPE.body.size,
          fontFamily: FONTS.body,
          color: COLORS.black,
          cursor: 'pointer',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 12px center',
        }}
      >
        {Object.entries(options).map(([key, data]) => (
          <option key={key} value={key}>
            {data.name} - {data.description}{recommendedKey === key ? ' (Recommended)' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

function Toggle({ label, description, checked, onChange, pulsate = false }) {
  return (
    <div
      className={pulsate && !checked ? 'pulsate-attention' : ''}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: SPACE.md,
        marginBottom: SPACE.md,
        padding: `${SPACE.md} ${SPACE.md}`,
        background: checked ? `${COLORS.orange}08` : COLORS.offWhite,
        borderRadius: FORM.inputRadius,
        cursor: 'pointer',
        border: checked ? `1px solid ${COLORS.orange}30` : '1px solid transparent',
        transition: 'all 0.2s ease',
      }}
      onClick={() => onChange(!checked)}
    >
      <div style={{
        width: 20,
        height: 20,
        borderRadius: 4,
        border: checked ? `2px solid ${COLORS.orange}` : `2px solid ${COLORS.lightGray}`,
        background: checked ? COLORS.orange : COLORS.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 2,
        transition: 'all 0.2s ease',
      }}>
        {checked && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )}
      </div>
      <div>
        <div style={{
          fontSize: TYPE.body.size,
          fontWeight: 500,
          color: COLORS.black,
          fontFamily: FONTS.body,
          marginBottom: SPACE.xs,
          lineHeight: TYPE.body.lineHeight,
        }}>
          {label}
        </div>
        {description && (
          <div style={{
            fontSize: TYPE.small.size,
            color: COLORS.gray,
            fontFamily: FONTS.body,
            lineHeight: TYPE.small.lineHeight,
          }}>
            {description}
          </div>
        )}
      </div>
    </div>
  );
}

function UrlAnalyzer({ onAnalyze, isAnalyzing, result, error, onClear }) {
  const [inputValue, setInputValue] = useState('');

  // Normalize URL to ensure it has https://
  const normalizeUrl = (url) => {
    let normalized = url.trim();
    if (!normalized) return '';
    
    // Remove any leading/trailing whitespace
    normalized = normalized.toLowerCase();
    
    // If it doesn't start with http:// or https://, add https://
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
      // Remove www. prefix if present for cleaner processing, then add it back with https://
      normalized = 'https://' + normalized;
    }
    
    return normalized;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      const normalizedUrl = normalizeUrl(inputValue);
      onAnalyze(normalizedUrl);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="url-analyzer-form" style={{ display: 'flex', gap: SPACE.sm }}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="yourcompany.com"
          disabled={isAnalyzing}
          style={{
            flex: 1,
            padding: FORM.inputPadding,
            borderRadius: FORM.inputRadius,
            border: `1px solid ${error ? '#ef4444' : COLORS.lightGray}`,
            background: COLORS.white,
            fontSize: TYPE.body.size,
            fontFamily: FONTS.body,
            color: COLORS.black,
          }}
        />
        <button
          type="submit"
          disabled={isAnalyzing || !inputValue.trim()}
          style={{
            padding: `${SPACE.md} ${SPACE.lg}`,
            borderRadius: FORM.inputRadius,
            border: 'none',
            background: isAnalyzing ? COLORS.gray : COLORS.orange,
            color: COLORS.white,
            fontSize: TYPE.body.size,
            fontWeight: 500,
            fontFamily: FONTS.body,
            cursor: isAnalyzing ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: SPACE.sm,
            minWidth: 110,
            justifyContent: 'center',
            opacity: (!inputValue.trim() && !isAnalyzing) ? 0.6 : 1,
          }}
        >
          {isAnalyzing ? (
            <>
              <span className="spinner" style={{
                width: 14,
                height: 14,
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: 'white',
                borderRadius: '50%',
                display: 'inline-block',
              }} />
              Analyzing
            </>
          ) : (
            'Analyze'
          )}
        </button>
      </form>

      {/* Analysis Result Badge */}
      {result && result.success && !result.isWebflow && (
        <div style={{
          marginTop: SPACE.md,
          padding: `${SPACE.md} ${SPACE.md}`,
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: FORM.inputRadius,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span style={{ fontSize: TYPE.body.size, color: '#166534', fontFamily: FONTS.body }}>
              Detected: <strong>{result.cmsName || 'Unknown CMS'}</strong>
              {result.pageCount?.total && (
                <> with ~{result.pageCount.total} pages</>
              )}
            </span>
          </div>
          <button
            onClick={onClear}
            style={{
              background: 'none',
              border: 'none',
              color: COLORS.gray,
              cursor: 'pointer',
              fontSize: TYPE.small.size,
              fontFamily: FONTS.body,
              padding: `${SPACE.xs} ${SPACE.sm}`,
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Webflow Detected */}
      {result && result.isWebflow && (
        <div style={{
          marginTop: SPACE.md,
          padding: `${SPACE.md} ${SPACE.md}`,
          background: '#dbeafe',
          border: '1px solid #93c5fd',
          borderRadius: FORM.inputRadius,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span style={{ fontSize: TYPE.body.size, color: '#1e40af', fontFamily: FONTS.body }}>
              Your site is already on Webflow! See below how we can help maintain and improve it.
            </span>
          </div>
        </div>
      )}

      {/* Partial Detection */}
      {result && result.success && !result.detectedCMS && !result.isWebflow && result.pageCount?.total && (
        <div style={{
          marginTop: SPACE.md,
          padding: `${SPACE.md} ${SPACE.md}`,
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: FORM.inputRadius,
        }}>
          <span style={{ fontSize: TYPE.body.size, color: '#92400e', fontFamily: FONTS.body }}>
            Found ~{result.pageCount.total} pages. Please select your current platform below.
          </span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          marginTop: SPACE.md,
          padding: `${SPACE.md} ${SPACE.md}`,
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: FORM.inputRadius,
        }}>
          <span style={{ fontSize: TYPE.body.size, color: '#991b1b', fontFamily: FONTS.body }}>
            {error}. You can still fill out the form manually below.
          </span>
        </div>
      )}
    </div>
  );
}

function DetectedBadge({ isDetected, isOverridden }) {
  if (!isDetected) return null;

  return (
    <span style={{
      marginLeft: SPACE.sm,
      padding: `2px ${SPACE.sm}`,
      borderRadius: 4,
      fontSize: TYPE.micro.size,
      fontWeight: 500,
      fontFamily: FONTS.body,
      background: isOverridden ? '#fef3c7' : '#dbeafe',
      color: isOverridden ? '#92400e' : '#1e40af',
    }}>
      {isOverridden ? 'Modified' : 'Auto-detected'}
    </span>
  );
}

// In-House vs Agency Calculator Component
const InHouseCalculator = forwardRef(function InHouseCalculator({
  calculations,
  selectedRoles,
  setSelectedRoles,
  selectedCity,
  setSelectedCity,
  benefitsMultiplier,
  setBenefitsMultiplier,
  isExpanded,
  setIsExpanded,
  companySize,
  aorTiers,
  selectedAorTierIndex,
  setSelectedAorTierIndex,
  isWebflowDetected = false,
}, ref) {
  // Get tiers for current company size
  const tiers = aorTiers[companySize] || aorTiers.midmarket;
  const selectedTier = tiers[selectedAorTierIndex] || tiers[0];
  
  // Compute in-house costs dynamically based on user selections
  const inHouseCosts = useMemo(() => {
    const cityMultiplier = SALARY_DATA.cities[selectedCity]?.multiplier || 1.0;
    const benefitsMult = 1 + (benefitsMultiplier / 100);
    
    // Build role breakdown from selected roles
    const roleBreakdown = Object.entries(selectedRoles)
      .filter(([, isSelected]) => isSelected)
      .map(([roleKey]) => {
        const role = SALARY_DATA.roles[roleKey];
        if (!role) return null;
        const adjustedSalary = Math.round(role.base * cityMultiplier);
        const withBenefits = Math.round(adjustedSalary * benefitsMult);
        return {
          key: roleKey,
          title: role.title,
          baseSalary: adjustedSalary,
          withBenefits,
        };
      })
      .filter(Boolean);
    
    const totalBaseSalary = roleBreakdown.reduce((sum, r) => sum + r.baseSalary, 0);
    const totalWithBenefits = roleBreakdown.reduce((sum, r) => sum + r.withBenefits, 0);
    const recruitingCosts = Math.round(totalBaseSalary * SALARY_DATA.recruitingCostPercent);
    
    return {
      roleBreakdown,
      totalBaseSalary,
      totalWithBenefits,
      recruitingCosts,
      firstYearTotal: totalWithBenefits + recruitingCosts,
      rampUpMonths: SALARY_DATA.rampUpMonths,
    };
  }, [selectedRoles, selectedCity, benefitsMultiplier]);

  // Use selected tier pricing (or 0 for custom pricing)
  const aorMonthly = selectedTier?.price || 0;
  const aorAnnual = aorMonthly * 12;
  const savings = inHouseCosts.totalWithBenefits - aorAnnual;
  const savingsPercent = inHouseCosts.totalWithBenefits > 0
    ? Math.round((savings / inHouseCosts.totalWithBenefits) * 100)
    : 0;

  const toggleRole = (roleKey) => {
    setSelectedRoles(prev => ({
      ...prev,
      [roleKey]: !prev[roleKey]
    }));
  };

  return (
    <div 
      ref={ref}
      className="section-card dark-theme-card aor-section" 
      style={{
        marginTop: SPACE['2xl'],
        background: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: CARD.radius,
        padding: CARD.padding.desktop,
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
      }}
    >
      {/* Main headline - styled like page title */}
      <h3 className="aor-section-headline" style={{
        fontSize: TYPE.display.size,
        fontWeight: TYPE.display.weight,
        fontFamily: FONTS.headline,
        color: COLORS.white,
        marginBottom: SPACE.md,
        lineHeight: TYPE.display.lineHeight,
        textAlign: 'left',
      }}>
        {isWebflowDetected 
          ? "You're already on Webflow — awesome! Let's show you how our AOR plans help you scale."
          : "Our partnerships don't end at launch."}
      </h3>

      {/* AOR Explainer Section */}
      <div style={{
        marginBottom: SPACE.xl,
        padding: CARD.padding.desktop,
        background: 'rgba(255, 255, 255, 0.04)',
        borderRadius: CARD.radius,
        border: '1px solid rgba(255, 255, 255, 0.08)',
      }}>
        <div style={{
          fontSize: TYPE.micro.size,
          fontWeight: 600,
          color: COLORS.orange,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontFamily: FONTS.body,
          marginBottom: SPACE.sm,
        }}>
          Agency of Record (AOR)
        </div>
        <p style={{
          fontSize: TYPE.h3.size,
          color: COLORS.white,
          fontFamily: FONTS.body,
          lineHeight: TYPE.h3.lineHeight,
          marginBottom: SPACE.md,
          marginTop: 0,
        }}>
          A flexible monthly partnership for continuous site improvements—design, development, and strategy.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: SPACE.md,
        }}>
          {[
            { icon: '⚡', text: 'Faster iteration on campaigns & landing pages' },
            { icon: '📈', text: 'Continuous conversion optimization' },
            { icon: '🔬', text: 'A/B testing & experiment velocity' },
            { icon: '✓', text: 'Full backlog coverage without hiring' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: SPACE.sm,
            }}>
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              <span style={{
                fontSize: TYPE.label.size,
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: FONTS.body,
                lineHeight: TYPE.label.lineHeight,
              }}>
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Dynamic savings subtitle */}
      <p style={{
        fontSize: TYPE.body.size,
        color: 'rgba(255, 255, 255, 0.6)',
        fontFamily: FONTS.body,
        marginBottom: SPACE.lg,
        lineHeight: TYPE.body.lineHeight,
        textAlign: 'left',
      }}>
        See your estimated annual savings with Azai as your Agency of Record vs hiring in-house.
      </p>

      {/* Section title */}
      <h4 style={{
        fontSize: TYPE.h3.size,
        fontWeight: TYPE.h3.weight,
        fontFamily: FONTS.body,
        color: COLORS.white,
        marginBottom: SPACE.lg,
        textAlign: 'left',
      }}>
        Choose an AOR plan for your {COMPANY_SIZES[companySize]?.name || 'company'}
      </h4>

      {/* AOR Tier Selection Cards */}
      <div className="aor-tier-grid" style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(200px, 1fr))`,
        gap: SPACE.md,
        marginBottom: SPACE.xl,
      }}>
        {tiers.map((tier, index) => (
          <div
            key={tier.name}
            onClick={() => setSelectedAorTierIndex(index)}
            style={{
              padding: CARD.padding.desktop,
              borderRadius: CARD.radius,
              border: `2px solid ${selectedAorTierIndex === index ? COLORS.orange : 'rgba(255, 255, 255, 0.15)'}`,
              background: selectedAorTierIndex === index ? 'rgba(255, 100, 34, 0.15)' : 'rgba(255, 255, 255, 0.04)',
              cursor: 'pointer',
              position: 'relative',
              transition: 'all 0.2s ease',
            }}
          >
            {tier.featured && tier.popularLabel && (
              <div style={{
                position: 'absolute',
                top: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                background: COLORS.orange,
                color: COLORS.white,
                fontSize: TYPE.micro.size,
                fontWeight: 600,
                padding: `${SPACE.xs} ${SPACE.md}`,
                borderRadius: 12,
                fontFamily: FONTS.body,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                {tier.popularLabel}
              </div>
            )}
            <div style={{
              fontSize: TYPE.h4.size,
              fontWeight: 600,
              color: selectedAorTierIndex === index ? COLORS.orange : COLORS.white,
              fontFamily: FONTS.body,
              marginBottom: SPACE.sm,
            }}>
              {tier.name}
            </div>
            <div style={{
              fontSize: TYPE.h2.size,
              fontWeight: 400,
              fontFamily: FONTS.headline,
              color: COLORS.white,
              marginBottom: SPACE.sm,
            }}>
              {tier.price ? formatCurrency(tier.price) : 'Custom'}
              {tier.price && <span style={{ fontSize: TYPE.body.size, color: 'rgba(255, 255, 255, 0.5)' }}>/mo</span>}
            </div>
            <div style={{
              fontSize: TYPE.small.size,
              color: 'rgba(255, 255, 255, 0.6)',
              fontFamily: FONTS.body,
              lineHeight: TYPE.small.lineHeight,
              marginBottom: SPACE.md,
            }}>
              {tier.description}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
              {tier.features.slice(0, 4).map((feature, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACE.sm,
                  fontSize: TYPE.small.size,
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontFamily: FONTS.body,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={COLORS.orange} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  {feature}
                </div>
              ))}
              {tier.features.length > 4 && (
                <div style={{ fontSize: TYPE.micro.size, color: 'rgba(255, 255, 255, 0.5)', fontFamily: FONTS.body, marginTop: SPACE.xs }}>
                  + {tier.features.length - 4} more benefits
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Expandable Team Customization */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className={!isExpanded ? 'pulsate-attention' : ''}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `${SPACE.lg} ${CARD.padding.desktop}`,
          background: 'rgba(255, 255, 255, 0.06)',
          borderRadius: CARD.radius,
          cursor: 'pointer',
          marginBottom: SPACE.lg,
        }}
      >
        <div>
          <div style={{ fontSize: TYPE.h4.size, fontWeight: 500, color: COLORS.white, fontFamily: FONTS.body }}>
            Customize Your Comparison
          </div>
          <div style={{ fontSize: TYPE.body.size, color: 'rgba(255, 255, 255, 0.6)', fontFamily: FONTS.body, marginTop: SPACE.xs }}>
            Select roles, location, and benefits to see your specific costs
          </div>
        </div>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: COLORS.orange,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </div>

      {isExpanded && (
        <div style={{ marginBottom: SPACE.xl }}>
          {/* Role Selection */}
          <div style={{ marginBottom: SPACE.xl }}>
            <div style={{ fontSize: TYPE.body.size, fontWeight: 600, color: COLORS.white, marginBottom: SPACE.md, fontFamily: FONTS.body }}>
              Select Roles You&apos;d Need to Hire
            </div>
            <div className="roles-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: SPACE.sm }}>
              {Object.entries(SALARY_DATA.roles).map(([key, role]) => (
                <div
                  key={key}
                  onClick={() => toggleRole(key)}
                  style={{
                    padding: SPACE.md,
                    border: `2px solid ${selectedRoles[key] ? COLORS.orange : 'rgba(255, 255, 255, 0.15)'}`,
                    borderRadius: CARD.radius,
                    cursor: 'pointer',
                    background: selectedRoles[key] ? 'rgba(255, 100, 34, 0.15)' : 'rgba(255, 255, 255, 0.04)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{
                        fontSize: TYPE.body.size,
                        fontWeight: 500,
                        color: selectedRoles[key] ? COLORS.orange : COLORS.white,
                        fontFamily: FONTS.body,
                        marginBottom: SPACE.xs,
                      }}>
                        {role.title}
                      </div>
                      <div style={{ fontSize: TYPE.small.size, color: 'rgba(255, 255, 255, 0.6)', fontFamily: FONTS.body }}>
                        {role.description}
                      </div>
                    </div>
                    <div style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      border: `2px solid ${selectedRoles[key] ? COLORS.orange : 'rgba(255, 255, 255, 0.3)'}`,
                      background: selectedRoles[key] ? COLORS.orange : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginLeft: SPACE.md,
                    }}>
                      {selectedRoles[key] && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* City Selection & Benefits */}
          <div className="settings-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: SPACE.lg }}>
            <div>
              <div style={{ fontSize: TYPE.body.size, fontWeight: 600, color: COLORS.white, marginBottom: SPACE.sm, fontFamily: FONTS.body }}>
                Office Location
              </div>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                style={{
                  width: '100%',
                  padding: FORM.inputPadding,
                  fontSize: TYPE.body.size,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: FORM.inputRadius,
                  background: 'rgba(255, 255, 255, 0.08)',
                  fontFamily: FONTS.body,
                  cursor: 'pointer',
                  color: COLORS.white,
                }}
              >
                {Object.entries(SALARY_DATA.cities).map(([key, city]) => (
                  <option key={key} value={key} style={{ background: '#1a1a1a', color: COLORS.white }}>{city.name}</option>
                ))}
              </select>
              <div style={{ fontSize: TYPE.small.size, color: 'rgba(255, 255, 255, 0.5)', marginTop: SPACE.sm, fontFamily: FONTS.body }}>
                Salary multiplier: {(SALARY_DATA.cities[selectedCity].multiplier * 100).toFixed(0)}% of US average
              </div>
            </div>
            <div>
              <div style={{ fontSize: TYPE.body.size, fontWeight: 600, color: COLORS.white, marginBottom: SPACE.sm, fontFamily: FONTS.body }}>
                Benefits & Overhead: {benefitsMultiplier}%
              </div>
              <input
                type="range"
                min={15}
                max={45}
                step={5}
                value={benefitsMultiplier}
                onChange={(e) => setBenefitsMultiplier(Number(e.target.value))}
                style={{
                  width: '100%',
                  height: 6,
                  borderRadius: 3,
                  appearance: 'none',
                  background: `linear-gradient(to right, ${COLORS.orange} 0%, ${COLORS.orange} ${((benefitsMultiplier - 15) / 30) * 100}%, rgba(255, 255, 255, 0.2) ${((benefitsMultiplier - 15) / 30) * 100}%, rgba(255, 255, 255, 0.2) 100%)`,
                  cursor: 'pointer',
                  marginTop: SPACE.sm,
                }}
              />
              <div style={{ fontSize: TYPE.small.size, color: 'rgba(255, 255, 255, 0.5)', marginTop: SPACE.sm, fontFamily: FONTS.body }}>
                Includes health insurance, 401k, PTO, taxes, equipment
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Results */}
      <div className="comparison-grid comparison-section" style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: SPACE.xl,
        alignItems: 'stretch',
        padding: CARD.padding.desktop,
        background: 'rgba(255, 255, 255, 0.06)',
        borderRadius: CARD.radius,
      }}>
        {/* In-House Column */}
        <div>
          <div style={{
            fontSize: TYPE.small.size,
            color: 'rgba(255, 255, 255, 0.5)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: SPACE.md,
            fontFamily: FONTS.body,
          }}>
            Build In-House Team
          </div>
          <div style={{
            fontSize: TYPE.display.size,
            fontWeight: 400,
            fontFamily: FONTS.headline,
            color: 'rgba(255, 255, 255, 0.5)',
            textDecoration: 'line-through',
            marginBottom: SPACE.lg,
          }}>
            {formatCurrency(inHouseCosts.totalWithBenefits)}<span style={{ fontSize: TYPE.h4.size }}>/yr</span>
          </div>

          {inHouseCosts.roleBreakdown.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
              {inHouseCosts.roleBreakdown.map((role, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: TYPE.label.size,
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontFamily: FONTS.body,
                }}>
                  <span>{role.title}</span>
                  <span>{formatCurrency(role.withBenefits)}</span>
                </div>
              ))}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: TYPE.label.size,
                color: 'rgba(255, 255, 255, 0.6)',
                fontFamily: FONTS.body,
                paddingTop: SPACE.sm,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                marginTop: SPACE.xs,
              }}>
                <span>+ First-year recruiting</span>
                <span>{formatCurrency(inHouseCosts.recruitingCosts)}</span>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: TYPE.body.size, color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic', fontFamily: FONTS.body }}>
              Select roles above to see costs
            </div>
          )}

          <div style={{
            marginTop: SPACE.lg,
            padding: SPACE.md,
            background: 'rgba(239, 68, 68, 0.15)',
            borderRadius: FORM.inputRadius,
            border: '1px solid rgba(239, 68, 68, 0.3)',
          }}>
            <div style={{ fontSize: TYPE.label.size, color: '#fca5a5', fontFamily: FONTS.body }}>
              <strong style={{ color: '#fca5a5' }}>Hidden Costs:</strong>
              <ul style={{ margin: `${SPACE.sm} 0 0 ${SPACE.md}`, padding: 0, lineHeight: 1.6 }}>
                <li>{SALARY_DATA.rampUpMonths} months ramp-up time</li>
                <li>~{Math.round(SALARY_DATA.turnoverRate * 100)}% annual turnover</li>
                <li>Management overhead</li>
                <li>Limited skill diversity</li>
              </ul>
            </div>
          </div>
        </div>

        {/* VS Divider */}
        <div className="vs-divider" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACE.sm,
        }}>
          <div className="vs-line" style={{ width: 1, height: '3.75rem', background: 'rgba(255, 255, 255, 0.15)' }} />
          <div style={{
            fontSize: TYPE.body.size,
            fontWeight: 600,
            color: COLORS.white,
            fontFamily: FONTS.body,
            padding: `${SPACE.sm} ${SPACE.md}`,
            background: COLORS.orange,
            borderRadius: '1.25rem',
            flexShrink: 0,
          }}>
            VS
          </div>
          <div className="vs-line" style={{ width: 1, height: '3.75rem', background: 'rgba(255, 255, 255, 0.15)' }} />
        </div>

        {/* Azai Column */}
        <div>
          <div style={{
            fontSize: TYPE.small.size,
            color: COLORS.orange,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: SPACE.md,
            fontFamily: FONTS.body,
          }}>
            Partner with Azai Studios
          </div>
          <div style={{
            fontSize: TYPE.display.size,
            fontWeight: 400,
            fontFamily: FONTS.headline,
            color: '#4ade80',
            marginBottom: SPACE.lg,
          }}>
            {formatCurrency(aorAnnual)}<span style={{ fontSize: TYPE.h4.size }}>/yr</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
            {[
              'Full creative & dev team on-demand',
              'No recruiting or hiring costs',
              'No benefits, PTO, or turnover',
              'Flexible scope month-to-month',
              'Diverse skills & fresh perspectives',
              'Start delivering in days, not months',
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACE.sm,
                fontSize: TYPE.label.size,
                color: COLORS.white,
                fontFamily: FONTS.body,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                {item}
              </div>
            ))}
          </div>

          <div style={{
            marginTop: SPACE.lg,
            padding: SPACE.md,
            background: 'rgba(74, 222, 128, 0.15)',
            borderRadius: FORM.inputRadius,
            border: '1px solid rgba(74, 222, 128, 0.3)',
          }}>
            <div style={{ fontSize: TYPE.label.size, color: '#86efac', fontFamily: FONTS.body }}>
              <strong style={{ color: '#86efac' }}>What You Get:</strong>
              <ul style={{ margin: `${SPACE.sm} 0 0 ${SPACE.md}`, padding: 0, lineHeight: 1.6 }}>
                <li>Strategy + Design + Development</li>
                <li>Dedicated account team</li>
                <li>24-48 hour response times</li>
                <li>Enterprise-level expertise</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Savings Summary */}
      {savings > 0 && (
        <div style={{
          marginTop: SPACE.lg,
          padding: `${SPACE.lg} ${SPACE.xl}`,
          background: 'rgba(74, 222, 128, 0.15)',
          border: '1px solid rgba(74, 222, 128, 0.3)',
          borderRadius: CARD.radius,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: SPACE.md,
          flexWrap: 'wrap',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
          <span style={{
            fontSize: TYPE.h3.size,
            fontWeight: 500,
            fontFamily: FONTS.headline,
            color: '#4ade80',
            textAlign: 'center',
          }}>
            Save {formatCurrency(savings)}+ per year ({savingsPercent}% less than in-house)
          </span>
        </div>
      )}
    </div>
  );
});

// ROI Projection Tool Component
function ROIProjectionTool({ 
  calculations, 
  selectedAorTierPrice,
  selectedAorTierName,
  selectedRoles,
  selectedCity,
  benefitsMultiplier
}) {
  const [timeframe, setTimeframe] = useState(3);

  const projections = useMemo(() => {
    const annualPlatformSavings = calculations.migration.annualPlatformSavings;
    const projectSavings = calculations.project.savingsVsOtherAgency;
    
    // Calculate in-house costs the same way as InHouseCalculator
    const cityMultiplier = SALARY_DATA.cities[selectedCity]?.multiplier || 1.0;
    const benefitsMult = 1 + (benefitsMultiplier / 100);
    
    const calculatedInHouseCost = Object.entries(selectedRoles)
      .filter(([, isSelected]) => isSelected)
      .reduce((sum, [roleKey]) => {
        const role = SALARY_DATA.roles[roleKey];
        if (!role) return sum;
        const adjustedSalary = Math.round(role.base * cityMultiplier);
        const withBenefits = Math.round(adjustedSalary * benefitsMult);
        return sum + withBenefits;
      }, 0);
    
    // Use calculated in-house cost, fallback to default
    const effectiveInHouseCost = calculatedInHouseCost > 0 ? calculatedInHouseCost : calculations.aor.inHouseAnnualCost;
    
    // AOR savings: only if a priced tier is selected
    const selectedAorAnnual = selectedAorTierPrice ? selectedAorTierPrice * 12 : 0;
    const aorSavings = selectedAorTierPrice && effectiveInHouseCost > selectedAorAnnual 
      ? effectiveInHouseCost - selectedAorAnnual 
      : 0;

    const years = [];
    let cumulative = 0;

    for (let year = 1; year <= 5; year++) {
      let yearSavings = 0;
      if (year === 1) {
        // Year 1: Platform savings + one-time project savings (vs other agency)
        yearSavings = projectSavings + annualPlatformSavings;
      } else {
        // Year 2+: Platform savings + AOR savings (vs in-house team)
        yearSavings = annualPlatformSavings + aorSavings;
      }
      cumulative += yearSavings;
      years.push({ 
        year, 
        yearSavings, 
        cumulative, 
        platformSavings: annualPlatformSavings, 
        aorSavings: year > 1 ? aorSavings : 0, 
        projectSavings: year === 1 ? projectSavings : 0 
      });
    }
    return years;
  }, [calculations, selectedAorTierPrice, selectedRoles, selectedCity, benefitsMultiplier]);

  const selectedData = projections.slice(0, timeframe);
  const totalSavings = selectedData[selectedData.length - 1]?.cumulative || 0;
  const maxSavings = projections[projections.length - 1]?.cumulative || 1;
  const initialInvestment = calculations.project.azaiTotal;
  const breakEvenYear = projections.find(p => p.cumulative >= initialInvestment)?.year || 1;

  return (
    <div className="section-card dark-theme-card" style={{
      marginTop: SPACE['2xl'],
      background: 'rgba(255, 255, 255, 0.04)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: CARD.radius,
      padding: CARD.padding.desktop,
    }}>
      <div style={{ textAlign: 'center', marginBottom: SPACE.xl }}>
        <div style={{
          fontSize: TYPE.label.size,
          color: COLORS.orange,
          marginBottom: SPACE.sm,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontWeight: 500,
          fontFamily: FONTS.body,
        }}>
          Financial Planning
        </div>
        <h3 style={{
          fontSize: TYPE.display.size,
          fontWeight: TYPE.display.weight,
          fontFamily: FONTS.headline,
          marginBottom: SPACE.md,
          lineHeight: TYPE.display.lineHeight,
          color: COLORS.white,
        }}>
          ROI Projection
        </h3>
        <p style={{
          fontSize: TYPE.body.size,
          color: 'rgba(255, 255, 255, 0.6)',
          lineHeight: TYPE.body.lineHeight,
          maxWidth: '37.5rem',
          margin: '0 auto',
          fontFamily: FONTS.body,
        }}>
          See how your savings compound over time with platform cost reductions and operational efficiencies.
        </p>
      </div>

      {/* Timeframe Toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: SPACE.sm, marginBottom: SPACE.xl, flexWrap: 'wrap' }}>
        {[1, 3, 5].map(years => (
          <button
            key={years}
            onClick={() => setTimeframe(years)}
            style={{
              padding: `${SPACE.md} ${SPACE.lg}`,
              background: timeframe === years ? COLORS.orange : 'rgba(255, 255, 255, 0.08)',
              border: 'none',
              borderRadius: FORM.inputRadius,
              fontSize: TYPE.body.size,
              fontWeight: 500,
              color: timeframe === years ? COLORS.white : 'rgba(255, 255, 255, 0.8)',
              cursor: 'pointer',
              fontFamily: FONTS.body,
            }}
          >
            {years} Year{years > 1 ? 's' : ''}
          </button>
        ))}
      </div>

      {/* Total Savings Highlight */}
      <div style={{
        textAlign: 'center',
        marginBottom: SPACE.xl,
        padding: CARD.padding.desktop,
        background: 'rgba(74, 222, 128, 0.15)',
        border: '1px solid rgba(74, 222, 128, 0.3)',
        borderRadius: CARD.radius,
      }}>
        <div style={{ fontSize: TYPE.body.size, color: '#86efac', marginBottom: SPACE.sm, fontFamily: FONTS.body }}>
          Total Projected Savings ({timeframe} Year{timeframe > 1 ? 's' : ''})
        </div>
        <div style={{
          fontSize: 'clamp(2.5rem, 8vw, 3rem)',
          fontWeight: 400,
          fontFamily: FONTS.headline,
          color: '#4ade80',
        }}>
          {formatCurrency(totalSavings)}
        </div>
        {breakEvenYear <= timeframe && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#86efac', fontFamily: FONTS.body }}>
            Break-even in Year {breakEvenYear} | Initial Investment: {formatCurrency(initialInvestment)}
          </div>
        )}
      </div>

      {/* Visual Chart */}
      <div style={{ marginBottom: '2rem' }}>
        <div className="roi-chart" style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: '1rem',
          height: 200,
          marginBottom: '1rem',
        }}>
          {selectedData.map((data) => (
            <div key={data.year} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: COLORS.white, fontWeight: 500, fontFamily: FONTS.body, marginBottom: '0.5rem' }}>
                {formatCurrency(data.cumulative)}
              </div>
              <div className="roi-bar" style={{
                width: '3.75rem',
                height: Math.max(20, (data.cumulative / maxSavings) * 180),
                background: `linear-gradient(to top, ${COLORS.orange}, ${COLORS.orange}cc)`,
                borderRadius: '0.5rem 0.5rem 0 0',
                position: 'relative',
              }}>
                {data.year === breakEvenYear && (
                  <div style={{
                    position: 'absolute',
                    top: -24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#4ade80',
                    color: '#052e16',
                    fontSize: 10,
                    padding: '2px 8px',
                    borderRadius: 10,
                    whiteSpace: 'nowrap',
                  }}>
                    Break-even
                  </div>
                )}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255, 255, 255, 0.6)', fontFamily: FONTS.body, marginTop: 8 }}>
                Year {data.year}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Year-by-Year Breakdown */}
      <div style={{ background: 'rgba(255, 255, 255, 0.06)', borderRadius: '0.625rem', padding: '1.5rem' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.white, marginBottom: '1rem', fontFamily: FONTS.body }}>
          Year-by-Year Breakdown
        </div>
        <div className="roi-table-wrapper" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONTS.body, fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.15)' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: COLORS.white }}>Year</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, color: COLORS.white }}>Platform Savings</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, color: COLORS.white }}>Project Savings</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, color: COLORS.white }}>AOR Savings</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, color: COLORS.white }}>Year Total</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, color: COLORS.orange }}>Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {selectedData.map(data => (
                <tr key={data.year} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <td style={{ padding: '12px 8px', fontWeight: 500, color: COLORS.white }}>Year {data.year}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{formatCurrency(data.platformSavings)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{data.projectSavings > 0 ? formatCurrency(data.projectSavings) : '—'}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{data.aorSavings > 0 ? formatCurrency(data.aorSavings) : '—'}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 500, color: COLORS.white }}>{formatCurrency(data.yearSavings)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, color: '#4ade80' }}>{formatCurrency(data.cumulative)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Migration Complexity Score Component (Light Theme - for white section)
function MigrationComplexityScore({ currentCMS, industry, staticPages, dynamicPages, needsRedesign, needsSeoAudit }) {
  const complexityFactors = useMemo(() => {
    const cmsData = CMS_DATA[currentCMS];
    const industryData = INDUSTRIES[industry];
    const totalPages = staticPages + dynamicPages;

    let factors = [];
    let totalScore = 0;

    // CMS complexity (1-3 points)
    let cmsScore = 1;
    if (cmsData?.category === 'Enterprise DXP') cmsScore = 3;
    else if (cmsData?.category === 'Headless CMS' || cmsData?.category === 'Open Source') cmsScore = 2;
    factors.push({ name: 'Source Platform', score: cmsScore, max: 3, description: cmsData?.name || 'Unknown CMS' });
    totalScore += cmsScore;

    // Page count complexity (1-3 points)
    let pageScore = 1;
    if (totalPages > 100) pageScore = 3;
    else if (totalPages > 50) pageScore = 2;
    factors.push({ name: 'Content Volume', score: pageScore, max: 3, description: `${totalPages} total pages` });
    totalScore += pageScore;

    // Dynamic content complexity (1-3 points)
    let dynamicScore = 1;
    if (dynamicPages > 20) dynamicScore = 3;
    else if (dynamicPages > 5) dynamicScore = 2;
    factors.push({ name: 'Dynamic Content', score: dynamicScore, max: 3, description: `${dynamicPages} CMS collections` });
    totalScore += dynamicScore;

    // Industry complexity (1-3 points)
    let industryScore = 1;
    if (industryData?.multiplier >= 1.5) industryScore = 3;
    else if (industryData?.multiplier >= 1.2) industryScore = 2;
    factors.push({ name: 'Industry Requirements', score: industryScore, max: 3, description: industryData?.name || 'General' });
    totalScore += industryScore;

    // Redesign complexity (0-2 points)
    let redesignScore = needsRedesign ? 2 : 0;
    factors.push({ name: 'Design Scope', score: redesignScore, max: 2, description: needsRedesign ? 'Full redesign' : 'Design lift' });
    totalScore += redesignScore;

    // SEO complexity (0-1 point)
    let seoScore = needsSeoAudit ? 1 : 0;
    factors.push({ name: 'SEO Requirements', score: seoScore, max: 1, description: needsSeoAudit ? 'Full audit needed' : 'Standard migration' });
    totalScore += seoScore;

    const maxScore = 15;
    const normalizedScore = Math.round((totalScore / maxScore) * 10);

    return { factors, totalScore, maxScore, normalizedScore };
  }, [currentCMS, industry, staticPages, dynamicPages, needsRedesign, needsSeoAudit]);

  // Light theme colors for risk levels
  const getRiskLevel = (score) => {
    if (score <= 3) return { label: 'Low', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
    if (score <= 5) return { label: 'Moderate', color: '#ca8a04', bg: '#fefce8', border: '#fef08a' };
    if (score <= 7) return { label: 'Moderate-High', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' };
    return { label: 'High', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
  };

  const riskLevel = getRiskLevel(complexityFactors.normalizedScore);

  return (
    <div className="section-card light-theme-card" style={{
      marginTop: '2rem',
      background: COLORS.white,
      border: `1px solid ${COLORS.lightGray}`,
      borderRadius: '0.625rem',
      padding: '2rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <div style={{
          fontSize: 13,
          color: COLORS.orange,
          marginBottom: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontWeight: 500,
          fontFamily: FONTS.body,
        }}>
          Project Assessment
        </div>
        <h3 style={{
          fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
          fontWeight: 400,
          fontFamily: FONTS.headline,
          marginBottom: '1rem',
          lineHeight: 1.2,
          color: COLORS.black,
        }}>
          Migration Complexity Score
        </h3>
        <p style={{
          fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
          color: COLORS.gray,
          lineHeight: 1.6,
          maxWidth: '37.5rem',
          margin: '0 auto',
          fontFamily: FONTS.body,
        }}>
          Based on your inputs, here&apos;s an assessment of your migration complexity to help set realistic expectations.
        </p>
      </div>

      {/* Main Score Display */}
      <div className="complexity-display" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '3rem', marginBottom: '2.5rem', flexWrap: 'wrap' }}>
        {/* Score Circle */}
        <div style={{ position: 'relative', width: 160, height: 160 }}>
          <svg width="160" height="160" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="70" fill="none" stroke={COLORS.lightGray} strokeWidth="12" />
            <circle
              cx="80" cy="80" r="70" fill="none" stroke={riskLevel.color} strokeWidth="12"
              strokeDasharray={`${(complexityFactors.normalizedScore / 10) * 440} 440`}
              strokeLinecap="round" transform="rotate(-90 80 80)"
            />
          </svg>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
            <div style={{ fontSize: 48, fontWeight: 400, fontFamily: FONTS.headline, color: riskLevel.color }}>
              {complexityFactors.normalizedScore}
            </div>
            <div style={{ fontSize: 14, color: COLORS.gray, fontFamily: FONTS.body }}>out of 10</div>
          </div>
        </div>

        {/* Risk Level */}
        <div>
          <div style={{ padding: '12px 24px', background: riskLevel.bg, border: `1px solid ${riskLevel.border}`, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 20, fontWeight: 500, color: riskLevel.color, fontFamily: FONTS.headline }}>
              {riskLevel.label} Complexity
            </div>
          </div>
          <div style={{ fontSize: 14, color: COLORS.gray, fontFamily: FONTS.body, maxWidth: 250 }}>
            {complexityFactors.normalizedScore <= 3 && 'Straightforward migration with minimal risk factors.'}
            {complexityFactors.normalizedScore > 3 && complexityFactors.normalizedScore <= 5 && 'Standard project with some areas requiring attention.'}
            {complexityFactors.normalizedScore > 5 && complexityFactors.normalizedScore <= 7 && 'Complex project requiring experienced team and careful planning.'}
            {complexityFactors.normalizedScore > 7 && 'Enterprise-grade project with multiple complexity factors.'}
          </div>
        </div>
      </div>

      {/* Factor Breakdown */}
      <div style={{ background: COLORS.offWhite, borderRadius: '0.625rem', padding: '1.5rem', marginBottom: '1.5rem', border: `1px solid ${COLORS.lightGray}` }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.black, marginBottom: '1.25rem', fontFamily: FONTS.body }}>
          Complexity Factors
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {complexityFactors.factors.map((factor, i) => (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: COLORS.black, fontFamily: FONTS.body }}>{factor.name}</span>
                  <span style={{ fontSize: 12, color: COLORS.gray, fontFamily: FONTS.body }}>({factor.description})</span>
                </div>
                <span style={{ fontSize: 13, color: COLORS.gray, fontFamily: FONTS.body }}>{factor.score}/{factor.max}</span>
              </div>
              <div style={{ height: 6, background: COLORS.lightGray, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${(factor.score / factor.max) * 100}%`,
                  background: factor.score >= factor.max * 0.66 ? '#dc2626' : factor.score >= factor.max * 0.33 ? '#ca8a04' : '#16a34a',
                  borderRadius: 3,
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div style={{ padding: '1.5rem', background: riskLevel.bg, borderRadius: '0.625rem', border: `1px solid ${riskLevel.border}` }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: riskLevel.color, marginBottom: '0.75rem', fontFamily: FONTS.body }}>
          Recommendations for Your Project
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {complexityFactors.normalizedScore <= 3 && (
            <>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Standard discovery and planning phase</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Expect smooth content migration</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Minimal custom development needed</div>
            </>
          )}
          {complexityFactors.normalizedScore > 3 && complexityFactors.normalizedScore <= 5 && (
            <>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Thorough discovery phase recommended</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Plan for integration testing time</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Content audit before migration</div>
            </>
          )}
          {complexityFactors.normalizedScore > 5 && complexityFactors.normalizedScore <= 7 && (
            <>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Extended discovery and planning phase</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Phased migration approach recommended</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Dedicated QA and testing resources</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Stakeholder alignment sessions</div>
            </>
          )}
          {complexityFactors.normalizedScore > 7 && (
            <>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Comprehensive discovery and architecture review</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Multi-phase migration with staging environments</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Dedicated project manager and QA team</div>
              <div style={{ fontSize: 13, color: COLORS.black, fontFamily: FONTS.body }}>• Compliance and security review required</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  // Ref for scrolling to AOR section
  const aorSectionRef = useRef(null);

  // Ref for dark section (for CTA styling)
  const darkSectionRef = useRef(null);

  // Ref for CTA form section
  const ctaFormRef = useRef(null);
  
  // State for CTA bar styling based on scroll position
  const [isInDarkSection, setIsInDarkSection] = useState(false);

  // Platform variant state (for PPC landing pages)
  const [platformVariant, setPlatformVariant] = useState(null);

  // State
  const [companySize, setCompanySize] = useState('enterprise');
  const [currentCMS, setCurrentCMS] = useState('wordpress');
  const [industry, setIndustry] = useState('saas');
  const [staticPages, setStaticPages] = useState(15);
  const [dynamicPages, setDynamicPages] = useState(5);
  const [needsRedesign, setNeedsRedesign] = useState(false);
  const [needsSeoAudit, setNeedsSeoAudit] = useState(false);

  // URL Analysis State
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [detectedValues, setDetectedValues] = useState({
    cms: null,
    staticPages: null,
    dynamicPages: null,
  });

  // In-House Calculator State
  const [selectedRoles, setSelectedRoles] = useState({
    uiuxDesigner: true,
    webflowDeveloper: true,
    graphicDesigner: true,
  });
  const [selectedCity, setSelectedCity] = useState('remote');
  const [benefitsMultiplier, setBenefitsMultiplier] = useState(30);
  const [isComparisonExpanded, setIsComparisonExpanded] = useState(false);
  const [selectedAorTierIndex, setSelectedAorTierIndex] = useState(0);
  
  // Budget range for qualification (soft gate)
  const [budgetRange, setBudgetRange] = useState('');

  // Toggle for including maintenance labor in platform cost comparison (Step 2)
  const [includeMaintenance, setIncludeMaintenance] = useState(false);

  // Toggle for showing AOR Plans section (hidden until user clicks "Explore AOR Plans")
  const [showAorPlans, setShowAorPlans] = useState(false);

  // Quote initiation state - tracks if user has engaged with the calculator
  const [hasStartedQuote, setHasStartedQuote] = useState(false);

  // URL section expansion state (collapsed by default for platform-specific landing pages)
  const [isUrlSectionExpanded, setIsUrlSectionExpanded] = useState(false);

  // Set default roles and reset AOR tier when company size changes
  useEffect(() => {
    // Reset AOR tier index to featured tier (or 0) when company size changes
    const tiers = AOR_TIERS[companySize] || AOR_TIERS.midmarket;
    const featuredIndex = tiers.findIndex(t => t.featured);
    setSelectedAorTierIndex(featuredIndex >= 0 ? featuredIndex : 0);
    
    // Set default roles based on company size
    const defaultRoles = {
      startup: {
        uiuxDesigner: true,
        webflowDeveloper: true,
        graphicDesigner: true,
        frontendDeveloper: false,
        projectManager: false,
        contentStrategist: false,
        brandStrategist: false,
        creativeDirector: false,
        paidMediaManager: false,
      },
      midmarket: {
        uiuxDesigner: true,
        webflowDeveloper: true,
        graphicDesigner: true,
        frontendDeveloper: false,
        projectManager: true,
        contentStrategist: true,
        brandStrategist: false,
        creativeDirector: false,
        paidMediaManager: false,
      },
      enterprise: {
        uiuxDesigner: true,
        webflowDeveloper: true,
        graphicDesigner: true,
        frontendDeveloper: true,
        projectManager: true,
        contentStrategist: false,
        brandStrategist: true,
        creativeDirector: true,
        paidMediaManager: false,
      },
      largeEnterprise: {
        uiuxDesigner: true,
        webflowDeveloper: true,
        graphicDesigner: true,
        frontendDeveloper: true,
        projectManager: true,
        contentStrategist: true,
        brandStrategist: true,
        creativeDirector: true,
        paidMediaManager: true,
      },
    };
    
    setSelectedRoles(defaultRoles[companySize] || defaultRoles.startup);
  }, [companySize]);

  // Read URL parameters for platform-specific landing pages (PPC campaigns)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const platform = params.get('platform');
    
    if (platform) {
      const variant = getVariantBySlug(platform);
      if (variant) {
        setPlatformVariant(variant);
        // Pre-select the CMS from the variant
        if (variant.cmsKey && CMS_DATA[variant.cmsKey]) {
          setCurrentCMS(variant.cmsKey);
        }
        // Set company size from variant for aligned messaging/CTAs
        if (variant.suggestedCompanySize) {
          setCompanySize(variant.suggestedCompanySize);
        }
        // Update page title for better Quality Score
        if (variant.metaTitle) {
          document.title = variant.metaTitle;
        }
      }
    }
  }, []); // Only run once on mount

  // Initialize tracking (Google Ads conversion tracking)
  useEffect(() => {
    const cleanup = initTracking();
    return cleanup;
  }, []);

  // ROI Projection State
  const [roiTimeframe, setRoiTimeframe] = useState(3);

  // FAQ State
  const [openFaq, setOpenFaq] = useState(null);

  // Lead Form State
  const [isCtaFormOpen, setIsCtaFormOpen] = useState(false);
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    annualRevenue: '',
    notes: '',
    interestedInAOR: false,
  });
  const [showEmailNudge, setShowEmailNudge] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [showGuideConfirmation, setShowGuideConfirmation] = useState(false);
  const [analyzedUrl, setAnalyzedUrl] = useState(null);
  
  // Scroll detection for CTA bar styling
  useEffect(() => {
    const handleScroll = () => {
      if (darkSectionRef.current) {
        const rect = darkSectionRef.current.getBoundingClientRect();
        // CTA is at bottom of viewport, so check if dark section top is above viewport bottom
        const viewportHeight = window.innerHeight;
        const ctaBarHeight = 80; // approximate height of sticky CTA
        // Consider "in dark section" when the dark section overlaps with the CTA bar position
        const isOverlapping = rect.top < (viewportHeight - ctaBarHeight) && rect.bottom > (viewportHeight - ctaBarHeight);
        setIsInDarkSection(isOverlapping);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to AOR section when showAorPlans becomes true
  useEffect(() => {
    if (showAorPlans && aorSectionRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        aorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Add highlight effect
        if (aorSectionRef.current) {
          aorSectionRef.current.classList.add('aor-highlight');
          setTimeout(() => {
            aorSectionRef.current?.classList.remove('aor-highlight');
          }, 1500);
        }
      }, 100);
    }
  }, [showAorPlans]);

  // Track if user has manually overridden detected values
  const hasOverriddenCms = detectedValues.cms && currentCMS !== detectedValues.cms;
  const hasOverriddenStatic = detectedValues.staticPages && staticPages !== detectedValues.staticPages;
  const hasOverriddenDynamic = detectedValues.dynamicPages && dynamicPages !== detectedValues.dynamicPages;

  // URL Analysis Handler
  const handleAnalyzeSite = async (url) => {
    setIsAnalyzing(true);
    setAnalysisError(null);
    setAnalysisResult(null);
    setHasStartedQuote(true); // User has engaged with the calculator
    
    // Track URL analyze event
    trackUrlAnalyze(url);
    markCalculatorInteraction();

    try {
      const response = await fetch('/api/analyze-site', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      const data = await response.json();

      if (data.success) {
        setAnalysisResult(data);
        setAnalyzedUrl(data.analyzedUrl || url);

        // Send notification email about the submission (fire and forget)
        fetch('/api/notify-submission', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            url: data.analyzedUrl || url,
            detectedCMS: data.detectedCMS,
            cmsName: data.cmsName,
            pageCount: data.pageCount,
            timestamp: new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
          }),
        }).catch(() => {}); // Silently ignore errors

        // Handle Webflow detection - scroll to Trusted By section (dark section)
        if (data.isWebflow) {
          setTimeout(() => {
            darkSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
          return;
        }

        // Auto-fill detected values
        if (data.detectedCMS && CMS_DATA[data.detectedCMS]) {
          // Find the right company size for this CMS
          const cmsData = CMS_DATA[data.detectedCMS];
          if (cmsData?.companyTier && COMPANY_SIZES[cmsData.companyTier]) {
            setCompanySize(cmsData.companyTier);
          }
          setCurrentCMS(data.detectedCMS);
          setDetectedValues(prev => ({ ...prev, cms: data.detectedCMS }));
        }

        // Auto-fill page counts
        if (data.pageCount) {
          if (data.pageCount.staticEstimate) {
            const staticVal = Math.min(100, Math.max(5, data.pageCount.staticEstimate));
            setStaticPages(staticVal);
            setDetectedValues(prev => ({ ...prev, staticPages: staticVal }));
          }
          if (data.pageCount.dynamicEstimate !== null && data.pageCount.dynamicEstimate !== undefined) {
            const dynamicVal = Math.min(50, Math.max(0, data.pageCount.dynamicEstimate));
            setDynamicPages(dynamicVal);
            setDetectedValues(prev => ({ ...prev, dynamicPages: dynamicVal }));
          }
        }
      } else {
        setAnalysisError(data.error || 'Unable to analyze site');
      }
    } catch (err) {
      setAnalysisError('Network error. Please check the URL and try again');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClearAnalysis = () => {
    setAnalysisResult(null);
    setAnalysisError(null);
    setDetectedValues({ cms: null, staticPages: null, dynamicPages: null });
    setAnalyzedUrl(null);
  };

  // Lead form handlers
  const handleLeadFormChange = (field, value) => {
    setLeadFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLeadFormSubmit = async (e) => {
    e.preventDefault();
    if (!leadFormData.name.trim() || !leadFormData.email.trim()) return;

    setIsSubmittingLead(true);
    
    // Get attribution data for form submission
    const attribution = getAttribution();
    
    // Track form submit attempt (non-PII data only)
    trackFormSubmit({
      platform: currentCMS,
      quoteValue: calculations.project.azaiTotal,
      companySize,
      industry,
      hasPhone: Boolean(leadFormData.phone.trim()),
      hasMessage: Boolean(leadFormData.notes.trim()),
      interestedInAor: leadFormData.interestedInAOR,
    });

    try {
      await fetch('/api/submit-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadFormData,
          budgetRange,
          websiteUrl: analyzedUrl,
          currentCMS,
          cmsName: CMS_DATA[currentCMS]?.name,
          industry,
          industryName: INDUSTRIES[industry]?.name,
          companySize,
          companySizeName: COMPANY_SIZES[companySize]?.name,
          staticPages,
          dynamicPages,
          needsRedesign,
          needsSeoAudit,
          estimatedProjectCost: calculations.project.azaiTotal,
          annualSavings: calculations.migration.annualPlatformSavings,
          buildWeeks: calculations.project.azaiBuildWeeks,
          // Attribution data
          gclid: attribution.gclid || null,
          utmSource: attribution.utm_source || null,
          utmMedium: attribution.utm_medium || null,
          utmCampaign: attribution.utm_campaign || null,
          utmContent: attribution.utm_content || null,
          utmTerm: attribution.utm_term || null,
        }),
      });
      
      // Track successful form submission
      trackFormSubmitSuccess();
    } catch (err) {
      // Track form error but still continue
      trackFormSubmitError('network');
      // Silently continue - we still want to redirect to calendar
    }

    // Fire GA4 recommended generate_lead event (key event for conversion tracking)
    trackGenerateLead({
      quoteValue: calculations.project.azaiTotal,
      platform: currentCMS,
      companySize,
      industry,
    });

    if (companySize === 'startup') {
      // SMB path: Show confirmation, no Calendly redirect
      // They get added to email nurture sequence via the API
      setIsSubmittingLead(false);
      setLeadFormData({ name: '', email: '', company: '', phone: '', annualRevenue: '', notes: '', interestedInAOR: false });
      setShowEmailNudge(false);
      setShowGuideConfirmation(true);
      // Auto-close confirmation after 8 seconds
      setTimeout(() => {
        setShowGuideConfirmation(false);
        setIsCtaFormOpen(false);
      }, 8000);
    } else {
      // Enterprise/Mid-Market path: Redirect to Google Calendar
      window.open(AGENCY_CONFIG.calendarUrl, '_blank');

      // Delay form reset slightly to ensure GA4 Enhanced Measurement
      // has time to detect the form submission event before DOM changes
      setTimeout(() => {
        setIsSubmittingLead(false);
        setIsCtaFormOpen(false);
        setLeadFormData({ name: '', email: '', company: '', phone: '', annualRevenue: '', notes: '', interestedInAOR: false });
        setShowEmailNudge(false);
      }, 500);
    }
  };

  const handleBookNowClick = () => {
    setIsCtaFormOpen(true);
    // Scroll to the CTA form section
    setTimeout(() => {
      ctaFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Calculations
  const calculations = useMemo(() => calculateProjectCosts({
    currentCMS,
    industry,
    companySize,
    staticPages,
    dynamicPages,
    needsSeoAudit,
    needsRedesign,
  }), [currentCMS, industry, companySize, staticPages, dynamicPages, needsSeoAudit, needsRedesign]);

  const cmsName = CMS_DATA[currentCMS].name;

  // Group all CMS options by category for better organization
  const availableCmsOptions = useMemo(() => {
    const grouped = {};
    Object.entries(CMS_DATA).forEach(([key, data]) => {
      const category = data.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push({ key, name: data.name, category });
    });
    return grouped;
  }, []);

  return (
    <div className="page-wrapper" style={{ background: COLORS.offWhite, minHeight: '100vh' }}>
      {/* Azai Studios Navigation */}
      <Nav />

      <main className="main-wrapper">
        <section className="section-calculator">
          <div className="padding-global">
            <div className="container-large">
              <div className="padding-section-large" style={{ paddingTop: '2rem' }}>
        {/* Header */}
        <header className="padding-bottom padding-medium" style={{ marginBottom: 0 }}>
          <h1 className="heading-style-h1" style={{
            fontSize: TYPE.display.size,
            fontWeight: TYPE.display.weight,
            fontFamily: FONTS.headline,
            color: COLORS.black,
            marginBottom: SPACE.md,
            lineHeight: TYPE.display.lineHeight,
          }}>
            {platformVariant?.headline || 'Enterprise CMS Migration Savings Calculator'}
          </h1>
          <p style={{
            fontSize: TYPE.h3.size,
            color: COLORS.gray,
            fontFamily: FONTS.body,
            maxWidth: '40rem',
            lineHeight: TYPE.h3.lineHeight,
          }}>
            {platformVariant?.subtitle || `See how much your organization could save by migrating from legacy platforms to Webflow — trusted by teams at Spotify, Microsoft, and Invesco.`}
          </p>
          {!platformVariant && (
            <p style={{
              fontSize: TYPE.small.size,
              color: COLORS.grayLight,
              fontFamily: FONTS.body,
              marginTop: SPACE.sm,
              letterSpacing: '0.02em',
            }}>
              Designed for organizations with $10M+ in annual revenue
            </p>
          )}
        </header>

        {/* Social Proof Bar - Visible on all pages including platform variants */}
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: SPACE.lg,
            padding: `${SPACE.md} ${SPACE.lg}`,
            background: COLORS.white,
            border: `1px solid ${COLORS.lightGray}`,
            borderRadius: CARD.radius,
            marginBottom: SPACE.lg,
          }}>
            {[
              { value: '50+', label: 'Enterprise Migrations' },
              { value: '$12M+', label: 'Client Savings' },
              { value: '8 wks', label: 'Avg. Timeline' },
              { value: '4.9', label: 'Client Rating', showStar: true },
            ].map((stat, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, textAlign: 'center' }}>
                <span style={{
                  fontSize: TYPE.h3.size,
                  fontWeight: 500,
                  fontFamily: FONTS.headline,
                  color: COLORS.black,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}>
                  {stat.value}
                  {stat.showStar && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={COLORS.orange} stroke="none">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  )}
                </span>
                <span style={{
                  fontSize: TYPE.small.size,
                  color: COLORS.gray,
                  fontFamily: FONTS.body,
                }}>
                  {stat.label}
                </span>
                {i < 3 && (
                  <span style={{
                    width: 1,
                    height: 24,
                    background: COLORS.lightGray,
                    marginLeft: SPACE.sm,
                  }} />
                )}
              </div>
            ))}
          </div>

        {/* Platform-specific value props (shown for PPC landing pages) */}
        {platformVariant?.valueProps && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: SPACE.md,
            marginBottom: SPACE.lg,
          }}>
            {platformVariant.valueProps.map((prop, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACE.sm,
                  padding: `${SPACE.sm} ${SPACE.md}`,
                  background: `${COLORS.orange}10`,
                  border: `1px solid ${COLORS.orange}30`,
                  borderRadius: FORM.inputRadius,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.orange} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span style={{
                  fontSize: TYPE.small.size,
                  color: COLORS.black,
                  fontFamily: FONTS.body,
                  fontWeight: 500,
                }}>
                  {prop}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* URL Analysis Section - Conditional display based on platform variant */}
        {platformVariant ? (
          // Collapsed version for platform-specific landing pages
          <section className="section-card" style={{
            background: COLORS.white,
            borderRadius: CARD.radius,
            padding: isUrlSectionExpanded ? CARD.padding.desktop : `${SPACE.md} ${SPACE.lg}`,
            marginBottom: SPACE.lg,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'padding 0.2s ease',
          }}>
            {!isUrlSectionExpanded ? (
              // Collapsed state - clickable to expand
              <div
                onClick={() => setIsUrlSectionExpanded(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  gap: SPACE.md,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.gray} strokeWidth="2" style={{ flexShrink: 0 }}>
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                  <span style={{
                    fontSize: TYPE.small.size,
                    color: COLORS.gray,
                    fontFamily: FONTS.body,
                    lineHeight: TYPE.small.lineHeight,
                  }}>
                    We&apos;ve pre-configured this estimate for <strong style={{ color: COLORS.black }}>{CMS_DATA[platformVariant.cmsKey]?.name || 'your platform'}</strong> sites.
                    {' '}Want more accurate page counts?
                  </span>
                </div>
                <span style={{
                  fontSize: TYPE.small.size,
                  color: COLORS.orange,
                  fontFamily: FONTS.body,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACE.xs,
                }}>
                  Enter your URL
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.orange} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </span>
              </div>
            ) : (
              // Expanded state - full URL analyzer
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: SPACE.sm, marginBottom: SPACE.md, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACE.sm }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={COLORS.orange} strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }}>
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <span style={{
                      fontSize: TYPE.body.size,
                      fontWeight: 400,
                      color: COLORS.black,
                      fontFamily: FONTS.body,
                      lineHeight: TYPE.body.lineHeight,
                    }}>
                      Enter your URL for more accurate page count detection.
                    </span>
                  </div>
                  <button
                    onClick={() => setIsUrlSectionExpanded(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: SPACE.xs,
                      cursor: 'pointer',
                      color: COLORS.gray,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    aria-label="Collapse URL section"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                  </button>
                </div>

                <UrlAnalyzer
                  onAnalyze={handleAnalyzeSite}
                  isAnalyzing={isAnalyzing}
                  result={analysisResult}
                  error={analysisError}
                  onClear={handleClearAnalysis}
                />
              </>
            )}
          </section>
        ) : (
          // Full URL section for normal visitors (no platform parameter)
          <section className="section-card" style={{
            background: COLORS.white,
            borderRadius: CARD.radius,
            padding: CARD.padding.desktop,
            marginBottom: SPACE.lg,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACE.sm, marginBottom: SPACE.md, flexWrap: 'wrap' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={COLORS.orange} strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <span style={{
                fontSize: TYPE.body.size,
                fontWeight: 400,
                color: COLORS.black,
                fontFamily: FONTS.body,
                lineHeight: TYPE.body.lineHeight,
              }}>
                You can simply start by entering in your website URL (we&apos;ll auto-detect your platform and page count), or jump to step 1.
              </span>
            </div>

            <UrlAnalyzer
              onAnalyze={handleAnalyzeSite}
              isAnalyzing={isAnalyzing}
              result={analysisResult}
              error={analysisError}
              onClear={handleClearAnalysis}
            />
          </section>
        )}

        {/* Calculator form and results */}
        <div className="calculator-main-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 420px) 1fr', gap: SPACE.xl }}>
          {/* Left column - Inputs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg }}>
            {/* Section 1: Your Organization */}
            <div className="section-card input-card-section" style={{
              background: COLORS.white,
              borderRadius: CARD.radius,
              padding: CARD.padding.desktop,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              <SectionHeader title="Your Organization" step="1" />
              <SelectDropdown
                label="Company Size"
                value={companySize}
                onChange={(newSize) => {
                  setCompanySize(newSize);
                  setHasStartedQuote(true);
                  trackCalculatorInteraction('company_size', newSize);
                  markCalculatorInteraction();
                }}
                options={COMPANY_SIZES}
                recommendedKey="enterprise"
              />
              <SelectDropdown
                label="Industry"
                value={industry}
                onChange={(val) => { 
                  setIndustry(val); 
                  setHasStartedQuote(true); 
                  trackCalculatorInteraction('industry', val);
                  markCalculatorInteraction();
                }}
                options={INDUSTRIES}
              />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: FORM.labelMargin }}>
                  <label style={{
                    fontSize: TYPE.label.size,
                    color: COLORS.gray,
                    fontWeight: 400,
                    fontFamily: FONTS.body,
                  }}>
                    Current Platform
                  </label>
                  <DetectedBadge isDetected={!!detectedValues.cms} isOverridden={hasOverriddenCms} />
                </div>
                <select
                  value={currentCMS}
                  onChange={(e) => { 
                    setCurrentCMS(e.target.value); 
                    setHasStartedQuote(true); 
                    trackCalculatorInteraction('current_cms', e.target.value);
                    markCalculatorInteraction();
                  }}
                  style={{
                    width: '100%',
                    padding: FORM.inputPadding,
                    borderRadius: FORM.inputRadius,
                    border: `1px solid ${COLORS.lightGray}`,
                    background: COLORS.white,
                    fontSize: TYPE.body.size,
                    fontFamily: FONTS.body,
                    color: COLORS.black,
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                  }}
                >
                  {Object.entries(availableCmsOptions).map(([category, platforms]) => (
                    <optgroup key={category} label={category}>
                      {platforms.map(({ key, name }) => (
                        <option key={key} value={key}>
                          {name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Budget Range - Soft qualification */}
              <div style={{ marginBottom: 0 }}>
                <label style={{
                  fontSize: TYPE.label.size,
                  color: COLORS.gray,
                  fontWeight: 400,
                  display: 'block',
                  marginBottom: FORM.labelMargin,
                  fontFamily: FONTS.body,
                  lineHeight: TYPE.label.lineHeight,
                }}>
                  Estimated Budget Range
                </label>
                <select
                  value={budgetRange}
                  onChange={(e) => {
                    setBudgetRange(e.target.value);
                    setHasStartedQuote(true);
                    trackCalculatorInteraction('budget_range', e.target.value);
                    markCalculatorInteraction();
                  }}
                  style={{
                    width: '100%',
                    padding: FORM.inputPadding,
                    borderRadius: FORM.inputRadius,
                    border: `1px solid ${COLORS.lightGray}`,
                    background: COLORS.white,
                    fontSize: TYPE.body.size,
                    fontFamily: FONTS.body,
                    color: budgetRange ? COLORS.black : COLORS.gray,
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                  }}
                >
                  <option value="" disabled>Select a range</option>
                  <option value="under_10k">Under $10K</option>
                  <option value="10k_25k">$10K - $25K</option>
                  <option value="25k_75k">$25K - $75K</option>
                  <option value="75k_150k">$75K - $150K</option>
                  <option value="150k_plus">$150K+</option>
                </select>
                {(budgetRange === 'under_10k' || budgetRange === '10k_25k') && (
                  <div style={{
                    marginTop: SPACE.sm,
                    padding: `${SPACE.sm} ${SPACE.md}`,
                    background: `${COLORS.orange}08`,
                    border: `1px solid ${COLORS.orange}20`,
                    borderRadius: FORM.inputRadius,
                    fontSize: TYPE.small.size,
                    color: COLORS.gray,
                    fontFamily: FONTS.body,
                    lineHeight: 1.5,
                  }}>
                    Based on your budget, we'd recommend exploring our self-service migration resources.{' '}
                    <button
                      type="button"
                      onClick={() => {
                        trackCtaClick('download_guide_budget_gate', { budgetRange });
                        handleBookNowClick();
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.orange,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: FONTS.body,
                        fontSize: TYPE.small.size,
                        padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      Download our free guide to get started
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Site Complexity */}
            <div className="section-card input-card-section" style={{
              background: COLORS.white,
              borderRadius: CARD.radius,
              padding: CARD.padding.desktop,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              <SectionHeader title="Site Complexity & Scope" step="2" />
              <div style={{ marginBottom: FORM.fieldGap }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE.sm, alignItems: 'flex-start', gap: SPACE.sm }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: SPACE.xs, minWidth: 0, flex: 1 }}>
                    <label style={{
                      fontSize: TYPE.label.size,
                      color: COLORS.gray,
                      fontWeight: 400,
                      fontFamily: FONTS.body,
                    }}>
                      Static pages (About, Contact, etc.)
                    </label>
                    <DetectedBadge isDetected={!!detectedValues.staticPages} isOverridden={hasOverriddenStatic} />
                  </div>
                  <span style={{
                    fontSize: TYPE.body.size,
                    color: COLORS.black,
                    fontWeight: 600,
                    fontFamily: FONTS.body,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}>
                    {staticPages} pages
                  </span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={100}
                  step={1}
                  value={staticPages}
                  onChange={(e) => { 
                    const newValue = Number(e.target.value);
                    setStaticPages(newValue); 
                    setHasStartedQuote(true); 
                    trackCalculatorInteraction('static_pages', newValue);
                    markCalculatorInteraction();
                  }}
                  style={{
                    width: '100%',
                    height: 6,
                    borderRadius: 3,
                    background: `linear-gradient(to right, ${COLORS.orange} 0%, ${COLORS.orange} ${((staticPages - 5) / (100 - 5)) * 100}%, ${COLORS.lightGray} ${((staticPages - 5) / (100 - 5)) * 100}%, ${COLORS.lightGray} 100%)`,
                    appearance: 'none',
                    cursor: 'pointer',
                  }}
                />
              </div>
              <div style={{ marginBottom: FORM.fieldGap }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE.sm, alignItems: 'flex-start', gap: SPACE.sm }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: SPACE.xs, minWidth: 0, flex: 1 }}>
                    <label style={{
                      fontSize: TYPE.label.size,
                      color: COLORS.gray,
                      fontWeight: 400,
                      fontFamily: FONTS.body,
                    }}>
                      Dynamic pages (Blog, Products, etc.)
                    </label>
                    <DetectedBadge isDetected={!!detectedValues.dynamicPages} isOverridden={hasOverriddenDynamic} />
                  </div>
                  <span style={{
                    fontSize: TYPE.body.size,
                    color: COLORS.black,
                    fontWeight: 600,
                    fontFamily: FONTS.body,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}>
                    {dynamicPages} templates
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={50}
                  step={1}
                  value={dynamicPages}
                  onChange={(e) => { 
                    const newValue = Number(e.target.value);
                    setDynamicPages(newValue); 
                    setHasStartedQuote(true); 
                    trackCalculatorInteraction('dynamic_pages', newValue);
                    markCalculatorInteraction();
                  }}
                  style={{
                    width: '100%',
                    height: 6,
                    borderRadius: 3,
                    background: `linear-gradient(to right, ${COLORS.orange} 0%, ${COLORS.orange} ${((dynamicPages - 0) / (50 - 0)) * 100}%, ${COLORS.lightGray} ${((dynamicPages - 0) / (50 - 0)) * 100}%, ${COLORS.lightGray} 100%)`,
                    appearance: 'none',
                    cursor: 'pointer',
                  }}
                />
              </div>
              <Toggle
                label="Site redesign needed"
                description="New visual design, not just migration"
                checked={needsRedesign}
                onChange={(val) => { 
                  setNeedsRedesign(val); 
                  setHasStartedQuote(true); 
                  trackCalculatorInteraction('needs_redesign', val);
                  markCalculatorInteraction();
                }}
                pulsate
              />
              <Toggle
                label="SEO audit & migration"
                description="Preserve rankings, redirects, metadata"
                checked={needsSeoAudit}
                onChange={(val) => { 
                  setNeedsSeoAudit(val); 
                  setHasStartedQuote(true); 
                  trackCalculatorInteraction('needs_seo_audit', val);
                  markCalculatorInteraction();
                }}
              />
            </div>
          </div>

          {/* Right column - Results */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg }}>
            {/* STEP 1: ONE-TIME MIGRATION COST */}
            <div className="section-card results-section" style={{
              background: COLORS.darkBg,
              borderRadius: CARD.radius,
              padding: CARD.padding.desktop,
              color: COLORS.white,
            }}>
              <div style={{
                display: 'inline-block',
                padding: `${SPACE.sm} ${SPACE.md}`,
                background: 'rgba(240,90,40,0.2)',
                borderRadius: 20,
                marginBottom: SPACE.md,
              }}>
                <span className="step-badge" style={{ fontSize: TYPE.small.size, fontWeight: 600, color: COLORS.orange, fontFamily: FONTS.body, letterSpacing: '0.05em' }}>
                  STEP 1: BUILD YOUR NEW SITE
                </span>
              </div>

              <div style={{ marginBottom: SPACE.lg }}>
                <div style={{ fontSize: TYPE.body.size, color: 'rgba(255,255,255,0.6)', fontFamily: FONTS.body, marginBottom: SPACE.sm }}>
                  One-time migration cost with {AGENCY_CONFIG.name}
                </div>
                {/* Price display - placeholder or actual value */}
                <div className={hasStartedQuote ? 'quote-value-animate' : ''} style={{
                  fontSize: 'clamp(2.5rem, 8vw, 3.5rem)',
                  fontWeight: 400,
                  fontFamily: FONTS.headline,
                  color: hasStartedQuote ? COLORS.white : 'rgba(255,255,255,0.3)',
                  lineHeight: 1.1,
                  transition: 'color 0.3s ease',
                }}>
                  {hasStartedQuote ? formatCurrency(calculations.project.azaiTotal) : '$XX,XXX'}
                </div>
                {/* Description - placeholder or actual */}
                <p style={{ 
                  fontSize: TYPE.body.size, 
                  color: hasStartedQuote ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)', 
                  fontFamily: FONTS.body, 
                  lineHeight: TYPE.body.lineHeight, 
                  marginTop: SPACE.sm,
                  transition: 'color 0.3s ease',
                }}>
                  {hasStartedQuote ? (
                    <>
                      Migrate from {cmsName} to Webflow in ~{calculations.project.azaiBuildWeeks} weeks.
                      {(() => {
                        const cmsPlatformOnly = calculations.currentCms.annualLicensing + calculations.currentCms.annualHosting;
                        const webflowPlatformOnly = calculations.webflowCms.annualLicensing;
                        const platformOnlySavings = cmsPlatformOnly - webflowPlatformOnly;
                        const displaySavings = includeMaintenance 
                          ? calculations.migration.annualPlatformSavings 
                          : platformOnlySavings;
                        if (displaySavings > 0) {
                          return (
                            <> You&apos;ll save <strong style={{ color: '#4ade80' }}>{formatCurrency(displaySavings)}/year</strong> {includeMaintenance ? 'on total platform costs' : 'on platform costs'}.</>
                          );
                        }
                        // When Webflow costs more, show maintenance hours saved or a softer benefit
                        const heroHoursSaved = calculations.currentCms.maintenanceHoursPerMonth - calculations.webflowCms.maintenanceHoursPerMonth;
                        if (heroHoursSaved > 0) {
                          return (
                            <> Save <strong style={{ color: '#60a5fa' }}>{heroHoursSaved} hrs/mo</strong> on maintenance.</> 
                          );
                        }
                        return null;
                      })()}
                    </>
                  ) : (
                    'Enter your website URL or adjust the options to see your personalized estimate.'
                  )}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }}>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: FORM.inputRadius, padding: SPACE.md }}>
                  <div style={{ fontSize: TYPE.label.size, color: 'rgba(255,255,255,0.5)', fontFamily: FONTS.body, marginBottom: SPACE.xs }}>
                    Timeline
                  </div>
                  <div style={{ 
                    fontSize: TYPE.h3.size, 
                    fontWeight: 500, 
                    fontFamily: FONTS.body,
                    color: hasStartedQuote ? COLORS.white : 'rgba(255,255,255,0.3)',
                    transition: 'color 0.3s ease',
                  }}>
                    {hasStartedQuote ? `~${calculations.project.azaiBuildWeeks} weeks` : '-- weeks'}
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: FORM.inputRadius, padding: SPACE.md }}>
                  <div style={{ fontSize: TYPE.label.size, color: 'rgba(255,255,255,0.5)', fontFamily: FONTS.body, marginBottom: SPACE.xs }}>
                    vs Other Agency
                  </div>
                  <div style={{ 
                    fontSize: TYPE.h3.size, 
                    fontWeight: 500, 
                    fontFamily: FONTS.body, 
                    color: hasStartedQuote ? '#4ade80' : 'rgba(255,255,255,0.3)',
                    transition: 'color 0.3s ease',
                  }}>
                    {hasStartedQuote ? `Save ${formatCurrency(calculations.project.savingsVsOtherAgency)}` : 'Save $X,XXX'}
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  trackCtaClick('get_started', { 
                    quoteValue: calculations.project.azaiTotal, 
                    platform: currentCMS 
                  });
                  handleBookNowClick();
                }}
                disabled={!hasStartedQuote}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACE.sm,
                  padding: `${SPACE.md} ${SPACE.xl}`,
                  background: hasStartedQuote ? COLORS.orange : 'rgba(240, 90, 40, 0.4)',
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: FORM.inputRadius,
                  fontSize: TYPE.body.size,
                  fontWeight: 500,
                  cursor: hasStartedQuote ? 'pointer' : 'default',
                  fontFamily: FONTS.body,
                  width: '100%',
                  marginTop: SPACE.lg,
                  opacity: hasStartedQuote ? 1 : 0.6,
                  transition: 'all 0.3s ease',
                }}
              >
                Get Started
              </button>

              {/* Enterprise confidence builders / SMB upsell */}
              {hasStartedQuote && (
                <div style={{
                  marginTop: SPACE.md,
                  padding: `${SPACE.sm} ${SPACE.md}`,
                  background: (companySize === 'enterprise' || companySize === 'largeEnterprise') 
                    ? 'rgba(74, 222, 128, 0.08)' 
                    : 'rgba(255,255,255,0.05)',
                  border: (companySize === 'enterprise' || companySize === 'largeEnterprise')
                    ? '1px solid rgba(74, 222, 128, 0.15)'
                    : 'none',
                  borderRadius: FORM.inputRadius,
                  textAlign: 'center',
                }}>
                  {(companySize === 'enterprise' || companySize === 'largeEnterprise') ? (
                    <span style={{ fontSize: TYPE.small.size, color: 'rgba(74, 222, 128, 0.8)', fontFamily: FONTS.body }}>
                      Includes dedicated PM, QA, and post-launch support
                    </span>
                  ) : (
                    <span style={{ fontSize: TYPE.small.size, color: 'rgba(255,255,255,0.5)', fontFamily: FONTS.body, lineHeight: 1.5 }}>
                      Want dedicated enterprise support for your migration?{' '}
                      <button
                        type="button"
                        onClick={() => setCompanySize('enterprise')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.orange,
                          fontWeight: 500,
                          cursor: 'pointer',
                          fontFamily: FONTS.body,
                          fontSize: TYPE.small.size,
                          padding: 0,
                          textDecoration: 'underline',
                        }}
                      >
                        Select Enterprise
                      </button>{' '}
                      to see our full-service options.
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* STEP 2: ONGOING PLATFORM COSTS */}
            {(() => {
              // Calculate platform-only costs (no maintenance labor)
              const cmsPlatformOnly = calculations.currentCms.annualLicensing + calculations.currentCms.annualHosting;
              const webflowPlatformOnly = calculations.webflowCms.annualLicensing;
              
              // Get maintenance details
              const cmsMaintenanceHours = calculations.currentCms.maintenanceHoursPerMonth;
              const webflowMaintenanceHours = calculations.webflowCms.maintenanceHoursPerMonth;
              const devHourlyRate = calculations.currentCms.devHourlyRate;
              const cmsMaintenanceCost = calculations.currentCms.annualMaintenance;
              const webflowMaintenanceCost = calculations.webflowCms.annualMaintenance;
              
              // Calculate hours saved
              const hoursSavedPerMonth = cmsMaintenanceHours - webflowMaintenanceHours;
              const maintenanceSavings = cmsMaintenanceCost - webflowMaintenanceCost;
              
              // Display values based on toggle
              const displayCmsCost = includeMaintenance 
                ? calculations.currentCms.totalAnnualCost 
                : cmsPlatformOnly;
              const displayWebflowCost = includeMaintenance 
                ? calculations.migration.webflowTotalAnnual 
                : webflowPlatformOnly;
              const displaySavings = displayCmsCost - displayWebflowCost;
              
              return (
                <div className="section-card results-section" style={{
                  background: COLORS.white,
                  borderRadius: CARD.radius,
                  padding: CARD.padding.desktop,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}>
                  <div style={{
                    display: 'inline-block',
                    padding: `${SPACE.sm} ${SPACE.md}`,
                    background: '#f0fdf4',
                    borderRadius: 20,
                    marginBottom: SPACE.md,
                  }}>
                    <span className="step-badge" style={{ fontSize: TYPE.small.size, fontWeight: 600, color: '#166534', fontFamily: FONTS.body, letterSpacing: '0.05em' }}>
                      STEP 2: ANNUAL PLATFORM COSTS
                    </span>
                  </div>

                  <div className="platform-costs-grid">
                    {/* Current CMS Column */}
                    <div className="platform-cost-column">
                      <div className="cost-label" style={{ fontSize: TYPE.label.size, color: COLORS.gray, fontFamily: FONTS.body }}>
                        Current {cmsName} Cost
                      </div>
                      <div className="cost-amount" style={{ fontFamily: FONTS.body, color: COLORS.black }}>
                        {formatCurrency(displayCmsCost)}/yr
                      </div>
                      
                      {/* Breakdown when toggle is on */}
                      {includeMaintenance && (
                        <div className="cost-breakdown" style={{ color: COLORS.gray, fontFamily: FONTS.body, fontSize: TYPE.small.size }}>
                          <div className="cost-breakdown-row">
                            <span>Platform costs:</span>
                            <span>{formatCurrency(cmsPlatformOnly)}</span>
                          </div>
                          <div className="cost-breakdown-row" style={{ color: '#b45309' }}>
                            <span>+ Maintenance labor:</span>
                            <span>{formatCurrency(cmsMaintenanceCost)}</span>
                          </div>
                          <div style={{ fontSize: TYPE.micro.size, color: '#9ca3af', marginTop: SPACE.xs }}>
                            ({cmsMaintenanceHours} hrs/mo × {formatCurrency(devHourlyRate)}/hr)
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Webflow Column */}
                    <div className="platform-cost-column">
                      <div className="cost-label" style={{ fontSize: TYPE.label.size, color: COLORS.gray, fontFamily: FONTS.body }}>
                        Webflow Cost
                      </div>
                      <div className="cost-amount" style={{ fontFamily: FONTS.body, color: '#166534' }}>
                        {formatCurrency(displayWebflowCost)}/yr
                      </div>
                      
                      {/* Breakdown when toggle is on */}
                      {includeMaintenance && (
                        <div className="cost-breakdown" style={{ color: COLORS.gray, fontFamily: FONTS.body, fontSize: TYPE.small.size }}>
                          <div className="cost-breakdown-row">
                            <span>Platform costs:</span>
                            <span>{formatCurrency(webflowPlatformOnly)}</span>
                          </div>
                          <div className="cost-breakdown-row" style={{ color: '#166534' }}>
                            <span>+ Maintenance labor:</span>
                            <span>{formatCurrency(webflowMaintenanceCost)}</span>
                          </div>
                          <div style={{ fontSize: TYPE.micro.size, color: '#9ca3af', marginTop: SPACE.xs }}>
                            ({webflowMaintenanceHours} hrs/mo × {formatCurrency(devHourlyRate)}/hr)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Toggle for including maintenance */}
                  <div 
                    className="maintenance-toggle"
                    style={{
                      marginTop: SPACE.lg,
                      padding: SPACE.md,
                      background: COLORS.offWhite,
                      borderRadius: FORM.inputRadius,
                      cursor: 'pointer',
                    }}
                    onClick={() => setIncludeMaintenance(!includeMaintenance)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
                      <div style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: `2px solid ${includeMaintenance ? COLORS.orange : COLORS.gray}`,
                        background: includeMaintenance ? COLORS.orange : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                      }}>
                        {includeMaintenance && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: TYPE.label.size, color: COLORS.black, fontFamily: FONTS.body }}>
                        Include estimated maintenance labor costs
                      </span>
                    </div>
                    <span className="toggle-hint" style={{ fontSize: TYPE.micro.size, color: COLORS.gray, fontFamily: FONTS.body }}>
                      (based on {companySize === 'startup' ? 'startup' : companySize === 'midmarket' ? 'mid-market' : companySize === 'enterprise' ? 'enterprise' : 'large enterprise'} rates)
                    </span>
                  </div>

                  {/* Savings message */}
                  {displaySavings > 0 ? (
                    <div 
                      className="savings-message"
                      style={{
                        marginTop: SPACE.lg,
                        padding: SPACE.md,
                        background: '#f0fdf4',
                        borderRadius: FORM.inputRadius,
                        textAlign: 'center',
                        border: '1px solid #bbf7d0',
                      }}
                    >
                      <span style={{ fontSize: TYPE.body.size, color: '#166534', fontFamily: FONTS.body, lineHeight: TYPE.body.lineHeight }}>
                        You&apos;ll save <strong style={{ fontSize: TYPE.h3.size }}>{formatCurrency(displaySavings)}</strong> every year
                        {includeMaintenance ? ' on total platform costs' : ' on platform costs'}
                      </span>
                      {includeMaintenance && hoursSavedPerMonth > 0 && (
                        <div style={{ fontSize: TYPE.small.size, color: '#166534', marginTop: SPACE.sm }}>
                          Webflow needs {hoursSavedPerMonth} fewer maintenance hours/month
                        </div>
                      )}
                    </div>
                  ) : displaySavings < 0 ? (
                    <div 
                      className="savings-message"
                      style={{
                        marginTop: SPACE.lg,
                        padding: SPACE.md,
                        background: '#eff6ff',
                        borderRadius: FORM.inputRadius,
                        border: '1px solid #bfdbfe',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ fontSize: TYPE.body.size, color: '#1e40af', fontFamily: FONTS.body, lineHeight: TYPE.body.lineHeight, marginBottom: SPACE.sm }}>
                        Webflow licensing is <strong>{formatCurrency(Math.abs(displaySavings))}/yr</strong> more{!includeMaintenance ? ' on platform alone' : ''}, but you&apos;ll gain:
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {hoursSavedPerMonth > 0 && (
                          <li style={{ fontSize: TYPE.small.size, color: '#1e40af', fontFamily: FONTS.body, lineHeight: 1.5 }}>
                            <strong>{hoursSavedPerMonth} fewer maintenance hours</strong> every month
                            {includeMaintenance && maintenanceSavings > 0 && (
                              <span style={{ color: '#166534' }}> (saving {formatCurrency(maintenanceSavings)}/yr in labor)</span>
                            )}
                          </li>
                        )}
                        {calculations.currentCms.changeTime !== 'Minutes' && (
                          <li style={{ fontSize: TYPE.small.size, color: '#1e40af', fontFamily: FONTS.body, lineHeight: 1.5 }}>
                            <strong>Instant content changes</strong> vs {calculations.currentCms.changeTime} on {calculations.currentCms.name}
                          </li>
                        )}
                        {calculations.currentCms.annualHosting > 0 && (
                          <li style={{ fontSize: TYPE.small.size, color: '#1e40af', fontFamily: FONTS.body, lineHeight: 1.5 }}>
                            <strong>Built-in hosting, SSL, and CDN</strong> — no separate bills
                          </li>
                        )}
                        <li style={{ fontSize: TYPE.small.size, color: '#1e40af', fontFamily: FONTS.body, lineHeight: 1.5 }}>
                          <strong>Visual editing</strong> — marketing ships without dev tickets
                        </li>
                        {(calculations.currentCms.category === 'Traditional CMS' || calculations.currentCms.category === 'Enterprise DXP') && (
                          <li style={{ fontSize: TYPE.small.size, color: '#1e40af', fontFamily: FONTS.body, lineHeight: 1.5 }}>
                            <strong>No plugin or security updates</strong> to manage
                          </li>
                        )}
                      </ul>
                      {!includeMaintenance && hoursSavedPerMonth > 0 && maintenanceSavings > Math.abs(displaySavings) && (
                        <div style={{ fontSize: TYPE.small.size, color: '#166534', marginTop: SPACE.sm, padding: `${SPACE.xs} ${SPACE.sm}`, background: '#f0fdf4', borderRadius: 6 }}>
                          Toggle maintenance costs above — the labor savings alone more than offset this difference.
                        </div>
                      )}
                    </div>
                  ) : null}

                  {/* Bridge to AOR - show when maintenance is visible */}
                  {includeMaintenance && cmsMaintenanceHours > 0 && (
                    <div 
                      className="bridge-card"
                      style={{
                        marginTop: 20,
                        padding: 16,
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderRadius: 8,
                        border: '1px solid #f59e0b',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', fontFamily: FONTS.body, marginBottom: 6, lineHeight: 1.4 }}>
                            Currently spending ~{cmsMaintenanceHours} hours/month on site maintenance?
                          </div>
                          <div style={{ fontSize: 12, color: '#a16207', fontFamily: FONTS.body, lineHeight: 1.5 }}>
                            Our Agency of Record plans handle all your maintenance, updates, and ongoing development. See Step 3 below.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* STEP 3: OPTIONAL AOR CTA CARD */}
            <div
              className="section-card results-section step3-cta-card"
              style={{
                background: COLORS.white,
                borderRadius: CARD.radius,
                padding: CARD.padding.desktop,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                border: `1px dashed ${COLORS.lightGray}`,
              }}
            >
              <div style={{
                display: 'inline-block',
                padding: `${SPACE.sm} ${SPACE.md}`,
                background: COLORS.offWhite,
                borderRadius: 20,
                marginBottom: SPACE.md,
              }}>
                <span className="step-badge" style={{ fontSize: TYPE.small.size, fontWeight: 600, color: COLORS.gray, fontFamily: FONTS.body, letterSpacing: '0.05em' }}>
                  STEP 3 (OPTIONAL)
                </span>
              </div>

              <h3 style={{ 
                fontSize: TYPE.h3.size, 
                fontWeight: TYPE.h3.weight, 
                fontFamily: FONTS.body, 
                color: COLORS.black,
                marginBottom: SPACE.sm,
                margin: 0,
              }}>
                Explore Agency of Record Support
              </h3>
              
              <p style={{ 
                fontSize: TYPE.body.size, 
                color: COLORS.gray, 
                fontFamily: FONTS.body, 
                lineHeight: TYPE.body.lineHeight,
                marginBottom: SPACE.lg,
                marginTop: SPACE.sm,
              }}>
                Ongoing design, development, and strategy after launch—without hiring an in-house team.
              </p>

              <button
                onClick={() => {
                  trackCtaClick('explore_aor', { 
                    quoteValue: calculations.project.azaiTotal, 
                    platform: currentCMS 
                  });
                  setShowAorPlans(true);
                }}
                style={{
                  width: '100%',
                  padding: `${SPACE.md} ${SPACE.lg}`,
                  background: COLORS.orange,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: FORM.inputRadius,
                  fontSize: TYPE.body.size,
                  fontWeight: 600,
                  fontFamily: FONTS.body,
                  cursor: 'pointer',
                  marginBottom: SPACE.md,
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) => e.target.style.background = '#d94d20'}
                onMouseLeave={(e) => e.target.style.background = COLORS.orange}
              >
                Explore AOR Plans
              </button>

              <div style={{ 
                fontSize: TYPE.small.size, 
                color: COLORS.gray, 
                fontFamily: FONTS.body, 
                textAlign: 'center',
              }}>
                See how much a <strong style={{ color: COLORS.black }}>{COMPANY_SIZES[companySize]?.name || 'company'}</strong> typically saves vs building an in-house team
              </div>
            </div>
          </div>
        </div>

        {/* Migration Complexity Score */}
        <MigrationComplexityScore
          currentCMS={currentCMS}
          industry={industry}
          staticPages={staticPages}
          dynamicPages={dynamicPages}
          needsRedesign={needsRedesign}
          needsSeoAudit={needsSeoAudit}
        />

        {/* Social Proof Section - Light Theme */}
        <div className="section-card light-theme-card" style={{
          marginTop: SPACE.xl,
          padding: CARD.padding.desktop,
          background: COLORS.white,
          border: `1px solid ${COLORS.lightGray}`,
          borderRadius: CARD.radius,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{
            fontSize: TYPE.label.size,
            fontWeight: 500,
            marginBottom: SPACE.xl,
            color: COLORS.black,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontFamily: FONTS.body,
            textAlign: 'center',
          }}>
            Trusted by Enterprise Teams
          </div>

          {/* Stats */}
          <div className="stats-grid-4" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: SPACE.xl,
            marginBottom: SPACE.xl,
          }}>
            {[
              { value: '50+', label: 'Enterprise migrations' },
              { value: '$12M+', label: 'Client savings delivered' },
              { value: '8 wks', label: 'Avg. migration time' },
              { value: '4.9', label: 'Client satisfaction', showStar: true },
            ].map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: TYPE.display.size,
                  fontWeight: 400,
                  fontFamily: FONTS.headline,
                  color: COLORS.black,
                  marginBottom: SPACE.xs,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.375rem',
                }}>
                  {stat.value}
                  {stat.showStar && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill={COLORS.orange} stroke="none">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  )}
                </div>
                <div style={{ fontSize: TYPE.label.size, color: COLORS.gray, fontFamily: FONTS.body }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* CMS logos */}
          <div style={{ paddingTop: SPACE.lg, borderTop: `1px solid ${COLORS.lightGray}` }}>
            <div style={{
              fontSize: TYPE.micro.size,
              color: COLORS.gray,
              fontFamily: FONTS.body,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: SPACE.md,
              textAlign: 'center',
            }}>
              Successfully migrated clients from
            </div>
            <div className="cms-badges" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: SPACE.sm,
              flexWrap: 'wrap',
            }}>
              {['Contentful', 'Sitecore', 'Adobe AEM', 'Drupal', 'WordPress VIP', 'Strapi'].map((cms) => (
                <span
                  key={cms}
                  style={{
                    fontSize: TYPE.label.size,
                    color: COLORS.gray,
                    fontFamily: FONTS.body,
                    padding: `${SPACE.sm} ${SPACE.md}`,
                    background: COLORS.offWhite,
                    border: `1px solid ${COLORS.lightGray}`,
                    borderRadius: '1.25rem',
                  }}
                >
                  {cms}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Why Azai Studios - Light Theme */}
        <div className="section-card light-theme-card" style={{
          marginTop: SPACE.xl,
          padding: CARD.padding.desktop,
          background: COLORS.white,
          border: `1px solid ${COLORS.lightGray}`,
          borderRadius: CARD.radius,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{
            fontSize: TYPE.label.size,
            fontWeight: 500,
            marginBottom: SPACE.sm,
            color: COLORS.orange,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontFamily: FONTS.body,
            textAlign: 'center',
          }}>
            Why Choose Us
          </div>
          <h3 style={{
            fontSize: TYPE.h2.size,
            fontWeight: TYPE.h2.weight,
            fontFamily: FONTS.headline,
            textAlign: 'center',
            marginBottom: SPACE.xl,
            color: COLORS.black,
            lineHeight: TYPE.h2.lineHeight,
          }}>
            {companySize === 'startup'
              ? `Why Teams Choose ${AGENCY_CONFIG.name}`
              : companySize === 'midmarket'
              ? `Why Growing Teams Choose ${AGENCY_CONFIG.name}`
              : `Why Enterprise Teams Choose ${AGENCY_CONFIG.name}`
            }
          </h3>

          <div className="features-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: SPACE.xl }}>
            {[
              { title: 'Webflow Experts', desc: "We specialize exclusively in Webflow. 100+ enterprise builds delivered with dedicated project teams and enterprise SLAs." },
              { title: 'Zero-Downtime Migrations', desc: "We've migrated sites from every major CMS — Sitecore, AEM, Contentful, Drupal. Our proven process ensures zero downtime and preserved SEO." },
              { title: 'Long-Term AOR Partnerships', desc: "We're not a one-and-done agency. Our enterprise AOR clients stay with us for years because we continuously drive results." },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: 'rgba(255, 100, 34, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: SPACE.md,
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.orange} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div style={{ fontSize: TYPE.h4.size, fontWeight: TYPE.h4.weight, color: COLORS.black, fontFamily: FONTS.body, marginBottom: SPACE.sm }}>
                  {item.title}
                </div>
                <div style={{ fontSize: TYPE.body.size, color: COLORS.gray, fontFamily: FONTS.body, lineHeight: TYPE.body.lineHeight, maxWidth: 280 }}>
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dark "Trusted By" Section - Visual Separation */}
        <div 
          ref={darkSectionRef}
          className="trusted-by-section" 
          style={{
            background: 'rgba(20, 20, 20, 0.98)',
            marginTop: SPACE['2xl'],
            position: 'relative',
          }}
        >
          {/* Client Marquee Section */}
          <div className="section-card" style={{
            padding: `${SPACE.xl} 0`,
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: CARD.radius,
            overflow: 'hidden',
          }}>
            <div style={{
              fontSize: TYPE.micro.size,
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontFamily: FONTS.body,
              textAlign: 'center',
              marginBottom: SPACE.lg,
              padding: `0 ${SPACE.xl}`,
            }}>
              Trusted By
            </div>
            <div className="marquee-container" style={{
              display: 'flex',
              overflow: 'hidden',
              width: '100%',
            }}>
              <div className="marquee-scroll" style={{
                display: 'flex',
                gap: SPACE['2xl'],
                animation: 'marqueeScroll 60s linear infinite',
                whiteSpace: 'nowrap',
              }}>
                {['Spotify', '605.TV', 'Microsoft', 'Invesco', 'AAK Capital', 'ADQ Abu Dhabi', 'Ahana Markets', 'Bond Communications', 'City of Saulkrasti', 'CRE8 Development Group', 'DAYNIGHT', 'Different & Able', 'DME Tuning', 'Energetech', 'EXPO 2020 Dubai', 'Ferronordic', 'Further Ventures', 'Gateway Partners', 'GILK', 'Hotel Chantelle', 'Ivira Health', 'IYLIA Wine', 'Kassin Sabbah Realty', 'LOKI Seltzers', 'Long-Term Stock Exchange', 'Main Technologies', 'Mars Markets', 'Meatpacking District NYC', 'Merlyn Mind', 'Microsoft', 'PIF', 'Ramar Capital', 'RIYA', 'Spotify', 'Synapse Fi', 'The Bridge NYC', 'The Charles NYC', 'Truly Secure', 'Urban Standard Capital', 'Vanilla Travel', 'Vision Realty Advisors', 'Volante Dubai', 'Wellware'].map((client, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: TYPE.body.size,
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontFamily: FONTS.body,
                      fontWeight: 400,
                      flexShrink: 0,
                    }}
                  >
                    {client}
                  </span>
                ))}
              </div>
              <div className="marquee-scroll" style={{
                display: 'flex',
                gap: SPACE['2xl'],
                animation: 'marqueeScroll 60s linear infinite',
                whiteSpace: 'nowrap',
              }}>
                {['Spotify', '605.TV', 'Microsoft', 'Invesco', 'AAK Capital', 'ADQ Abu Dhabi', 'Ahana Markets', 'Bond Communications', 'City of Saulkrasti', 'CRE8 Development Group', 'DAYNIGHT', 'Different & Able', 'DME Tuning', 'Energetech', 'EXPO 2020 Dubai', 'Ferronordic', 'Further Ventures', 'Gateway Partners', 'GILK', 'Hotel Chantelle', 'Ivira Health', 'IYLIA Wine', 'Kassin Sabbah Realty', 'LOKI Seltzers', 'Long-Term Stock Exchange', 'Main Technologies', 'Mars Markets', 'Meatpacking District NYC', 'Merlyn Mind', 'Microsoft', 'PIF', 'Ramar Capital', 'RIYA', 'Spotify', 'Synapse Fi', 'The Bridge NYC', 'The Charles NYC', 'Truly Secure', 'Urban Standard Capital', 'Vanilla Travel', 'Vision Realty Advisors', 'Volante Dubai', 'Wellware'].map((client, i) => (
                  <span
                    key={`dup-${i}`}
                    style={{
                      fontSize: TYPE.body.size,
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontFamily: FONTS.body,
                      fontWeight: 400,
                      flexShrink: 0,
                    }}
                  >
                    {client}
                  </span>
                ))}
              </div>
            </div>
          </div>

        {/* In-House vs Agency Calculator - Only shown when user clicks "Explore AOR Plans" */}
        {showAorPlans && (
          <>
            <InHouseCalculator
              ref={aorSectionRef}
              calculations={calculations}
              selectedRoles={selectedRoles}
              setSelectedRoles={setSelectedRoles}
              selectedCity={selectedCity}
              setSelectedCity={setSelectedCity}
              benefitsMultiplier={benefitsMultiplier}
              setBenefitsMultiplier={setBenefitsMultiplier}
              isExpanded={isComparisonExpanded}
              setIsExpanded={setIsComparisonExpanded}
              companySize={companySize}
              aorTiers={AOR_TIERS}
              selectedAorTierIndex={selectedAorTierIndex}
              setSelectedAorTierIndex={setSelectedAorTierIndex}
              isWebflowDetected={analysisResult?.isWebflow || false}
            />

            {/* ROI Projection Tool */}
            <ROIProjectionTool 
              calculations={calculations}
              selectedAorTierPrice={(AOR_TIERS[companySize] || AOR_TIERS.midmarket)[selectedAorTierIndex]?.price}
              selectedAorTierName={(AOR_TIERS[companySize] || AOR_TIERS.midmarket)[selectedAorTierIndex]?.name}
              selectedRoles={selectedRoles}
              selectedCity={selectedCity}
              benefitsMultiplier={benefitsMultiplier}
            />
          </>
        )}

          {/* Platform-Specific Testimonials (shown for PPC landing pages) */}
          {platformVariant?.testimonials && platformVariant.testimonials.length > 0 && (
            <section
              className="section-card"
              style={{
                marginTop: SPACE['2xl'],
                padding: CARD.padding.desktop,
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: CARD.radius,
              }}
            >
              <div style={{
                fontSize: TYPE.label.size,
                fontWeight: 500,
                marginBottom: SPACE.sm,
                color: COLORS.orange,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontFamily: FONTS.body,
                textAlign: 'center',
              }}>
                Success Stories
              </div>
              <h2 style={{
                fontSize: TYPE.h2.size,
                fontWeight: TYPE.h2.weight,
                fontFamily: FONTS.headline,
                textAlign: 'center',
                marginBottom: SPACE.xl,
                color: COLORS.white,
                lineHeight: TYPE.h2.lineHeight,
              }}>
                {CMS_DATA[platformVariant.cmsKey]?.name || 'Platform'} Migration Results
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: SPACE.xl,
                maxWidth: '60rem',
                margin: '0 auto',
              }}>
                {platformVariant.testimonials.map((testimonial, i) => (
                  <div
                    key={i}
                    style={{
                      padding: SPACE.xl,
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: CARD.radius,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Metric badge */}
                    {testimonial.metric && (
                      <div style={{
                        display: 'inline-flex',
                        alignSelf: 'flex-start',
                        padding: `${SPACE.xs} ${SPACE.md}`,
                        background: `${COLORS.orange}20`,
                        border: `1px solid ${COLORS.orange}40`,
                        borderRadius: 20,
                        marginBottom: SPACE.md,
                      }}>
                        <span style={{
                          fontSize: TYPE.small.size,
                          fontWeight: 600,
                          color: COLORS.orange,
                          fontFamily: FONTS.body,
                        }}>
                          {testimonial.metric}
                        </span>
                      </div>
                    )}
                    
                    {/* Quote */}
                    <blockquote style={{
                      fontSize: TYPE.body.size,
                      color: 'rgba(255, 255, 255, 0.85)',
                      fontFamily: FONTS.body,
                      lineHeight: 1.7,
                      margin: 0,
                      marginBottom: SPACE.lg,
                      fontStyle: 'italic',
                      flex: 1,
                    }}>
                      &ldquo;{testimonial.quote}&rdquo;
                    </blockquote>
                    
                    {/* Author */}
                    <div style={{
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      paddingTop: SPACE.md,
                    }}>
                      <div style={{
                        fontSize: TYPE.body.size,
                        fontWeight: 500,
                        color: COLORS.white,
                        fontFamily: FONTS.body,
                      }}>
                        {testimonial.author}
                      </div>
                      <div style={{
                        fontSize: TYPE.small.size,
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: FONTS.body,
                      }}>
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* FAQ Section */}
          <section
            aria-labelledby="faq-heading"
            className="section-card"
            style={{
              marginTop: SPACE['2xl'],
              padding: CARD.padding.desktop,
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: CARD.radius,
            }}
          >
            <div style={{
              fontSize: TYPE.label.size,
              fontWeight: 500,
              marginBottom: SPACE.sm,
              color: COLORS.orange,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontFamily: FONTS.body,
              textAlign: 'center',
            }}>
              Common Questions
            </div>
            <h2
              id="faq-heading"
              style={{
                fontSize: TYPE.h2.size,
                fontWeight: TYPE.h2.weight,
                fontFamily: FONTS.headline,
                textAlign: 'center',
                marginBottom: SPACE.xl,
                color: COLORS.white,
                lineHeight: TYPE.h2.lineHeight,
              }}
            >
              {platformVariant ? `${CMS_DATA[platformVariant.cmsKey]?.name || ''} Migration FAQ` : 'Frequently Asked Questions'}
            </h2>

            <div style={{ maxWidth: '50rem', margin: '0 auto' }}>
              {/* Platform-specific FAQs first, then general FAQs */}
              {[
                ...(platformVariant?.faqs?.map(faq => ({ q: faq.question, a: faq.answer, isPlatformSpecific: true })) || []),
                ...getFAQs(companySize, currentCMS, industry, CMS_DATA[currentCMS], INDUSTRIES[industry])
              ].map((faq, i) => (
                <div
                  key={i}
                  style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    style={{
                      width: '100%',
                      padding: `${SPACE.lg} 0`,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{
                      fontSize: TYPE.h4.size,
                      fontWeight: TYPE.h4.weight,
                      color: COLORS.white,
                      fontFamily: FONTS.body,
                      paddingRight: SPACE.md,
                      lineHeight: TYPE.h4.lineHeight,
                    }}>
                      {faq.q}
                    </span>
                    <div style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                  </button>
                  <div
                    style={{
                      paddingBottom: openFaq === i ? SPACE.lg : 0,
                      maxHeight: openFaq === i ? '500px' : '0',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <p style={{
                      fontSize: TYPE.body.size,
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontFamily: FONTS.body,
                      lineHeight: 1.7,
                      margin: 0,
                    }}>
                      {faq.a}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA - Expandable Form */}
          <div
            ref={ctaFormRef}
            className="section-card"
            style={{
              marginTop: SPACE['2xl'],
              padding: `${SPACE['2xl']} ${SPACE.xl}`,
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: CARD.radius,
              textAlign: 'center',
              overflow: 'hidden',
              transition: 'all 0.4s ease',
              position: 'relative',
            }}
          >
            {/* Collapsed State */}
            <div style={{
              maxHeight: isCtaFormOpen ? '0px' : '500px',
              opacity: isCtaFormOpen ? 0 : 1,
              overflow: 'hidden',
              transition: 'all 0.4s ease',
            }}>
              {!hasStartedQuote ? (
                /* Teaser state - shown before user starts quote */
                <>
                  <h3 style={{
                    fontSize: TYPE.display.size,
                    fontWeight: TYPE.display.weight,
                    fontFamily: FONTS.headline,
                    color: COLORS.white,
                    marginBottom: SPACE.md,
                    lineHeight: TYPE.display.lineHeight,
                  }}>
                    Get Your Free Migration Estimate
                  </h3>
                  <p style={{
                    fontSize: 16,
                    color: 'rgba(255,255,255,0.6)',
                    fontFamily: FONTS.body,
                    marginBottom: 32,
                    maxWidth: 500,
                    margin: '0 auto 32px',
                    lineHeight: 1.6,
                  }}>
                    Use the calculator above to see your personalized project estimate, timeline, and potential savings.
                  </p>
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                      padding: '18px 36px',
                      background: COLORS.orange,
                      color: COLORS.white,
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: FONTS.body,
                    }}
                  >
                    Start My Quote
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="19" x2="12" y2="5"></line>
                      <polyline points="5 12 12 5 19 12"></polyline>
                    </svg>
                  </button>
                </>
              ) : (
                /* Quote-based CTA - shown after user starts quote */
                <>
              <h3 style={{
                fontSize: TYPE.display.size,
                fontWeight: TYPE.display.weight,
                fontFamily: FONTS.headline,
                color: COLORS.white,
                marginBottom: SPACE.md,
                lineHeight: TYPE.display.lineHeight,
              }}>
                {companySize === 'startup'
                  ? "Get Your Free Migration Toolkit"
                  : companySize === 'midmarket'
                  ? `Ready to migrate from ${cmsName}?`
                  : `Ready to migrate from ${cmsName}?`
                }
              </h3>
              <p style={{
                fontSize: 16,
                color: 'rgba(255,255,255,0.6)',
                fontFamily: FONTS.body,
                marginBottom: 32,
                maxWidth: 500,
                margin: '0 auto 32px',
                lineHeight: 1.6,
              }}>
                {companySize === 'startup'
                  ? "Download our step-by-step migration guide, Webflow templates, and resource library — everything you need to plan your migration."
                  : companySize === 'midmarket'
                  ? "Book a free strategy call. We'll review your current setup, migration complexity, and create a roadmap tailored to your organization."
                  : "Schedule an executive briefing. We'll assess your infrastructure, present a comprehensive migration strategy, and outline projected ROI."
                }
              </p>
              <button
                onClick={() => {
                  trackCtaClick(companySize === 'startup' ? 'download_guide' : 'book_call', { 
                    quoteValue: calculations.project.azaiTotal, 
                    platform: currentCMS 
                  });
                  handleBookNowClick();
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: '18px 36px',
                  background: COLORS.orange,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: FONTS.body,
                }}
              >
                {companySize === 'startup' ? 'Download Free Guide' : companySize === 'midmarket' ? 'Book a Strategy Call' : 'Schedule Executive Briefing'}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {companySize === 'startup' ? (
                    <>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </>
                  ) : (
                    <>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </>
                  )}
                </svg>
              </button>
              <div style={{
                marginTop: 16,
                fontSize: 13,
                color: 'rgba(255,255,255,0.4)',
                fontFamily: FONTS.body,
              }}>
                {companySize === 'startup'
                  ? 'For hands-on enterprise migration support, companies typically invest $25K+ with Azai Studios.'
                  : companySize === 'midmarket'
                  ? 'Typical project investment: $25K - $75K'
                  : 'Typical project investment: $150K+'
                }
              </div>
                </>
              )}
            </div>

            {/* Expanded Form State */}
            <div style={{
              maxHeight: isCtaFormOpen ? '1200px' : '0px',
              opacity: isCtaFormOpen ? 1 : 0,
              overflow: 'hidden',
              transition: 'all 0.4s ease',
            }}>
              {/* Close button */}
              <button
                onClick={() => setIsCtaFormOpen(false)}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              <h3 style={{
                fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                fontWeight: 400,
                fontFamily: FONTS.headline,
                color: COLORS.white,
                marginBottom: '0.5rem',
              }}>
                {companySize === 'startup' ? 'Get Your Free Migration Toolkit' : companySize === 'midmarket' ? 'Book Your Strategy Call' : 'Schedule Your Executive Briefing'}
              </h3>
              <p style={{
                fontSize: 14,
                color: 'rgba(255,255,255,0.6)',
                fontFamily: FONTS.body,
                marginBottom: 24,
              }}>
                {companySize === 'startup'
                  ? "Enter your details below and we'll send you our complete migration toolkit."
                  : 'Tell us a bit about yourself so we can prepare for our call.'
                }
              </p>

              {/* Project Summary Pills */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                justifyContent: 'center',
                marginBottom: 24,
              }}>
                <span style={{
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: 20,
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: FONTS.body,
                }}>
                  {COMPANY_SIZES[companySize]?.name}
                </span>
                <span style={{
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: 20,
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: FONTS.body,
                }}>
                  {INDUSTRIES[industry]?.name}
                </span>
                <span style={{
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: 20,
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: FONTS.body,
                }}>
                  {CMS_DATA[currentCMS]?.name}
                </span>
                <span style={{
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: 20,
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: FONTS.body,
                }}>
                  {staticPages + dynamicPages} pages
                </span>
                {analyzedUrl && (
                  <span style={{
                    padding: '6px 12px',
                    background: `${COLORS.orange}20`,
                    borderRadius: 20,
                    fontSize: 12,
                    color: COLORS.orange,
                    fontFamily: FONTS.body,
                  }}>
                    {analyzedUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </span>
                )}
              </div>

              {/* Estimate Banner */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: 12,
                padding: '16px 24px',
                marginBottom: 24,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 24,
                flexWrap: 'wrap',
              }}>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                    Project Estimate
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 500, color: COLORS.orange, fontFamily: FONTS.headline }}>
                    {formatCurrency(calculations.project.azaiTotal)}
                  </div>
                </div>
                {(() => {
                  // Calculate platform-only savings
                  const cmsPlatformOnly = calculations.currentCms.annualLicensing + calculations.currentCms.annualHosting;
                  const webflowPlatformOnly = calculations.webflowCms.annualLicensing;
                  const platformOnlySavings = cmsPlatformOnly - webflowPlatformOnly;
                  
                  // Use toggle to determine which savings to show
                  const displaySavings = includeMaintenance 
                    ? calculations.migration.annualPlatformSavings 
                    : platformOnlySavings;
                  
                  if (displaySavings > 0) {
                    return (
                      <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 24 }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                          Annual Savings
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 500, color: '#4ade80', fontFamily: FONTS.headline }}>
                          {formatCurrency(displaySavings)}
                        </div>
                      </div>
                    );
                  }
                  // When platform savings are negative, show maintenance time saved or maintenance dollar savings
                  const leadHoursSaved = calculations.currentCms.maintenanceHoursPerMonth - calculations.webflowCms.maintenanceHoursPerMonth;
                  const leadMaintenanceSavings = calculations.currentCms.annualMaintenance - calculations.webflowCms.annualMaintenance;
                  if (leadMaintenanceSavings > 0 && includeMaintenance) {
                    return (
                      <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 24 }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                          Maintenance Savings
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 500, color: '#60a5fa', fontFamily: FONTS.headline }}>
                          {formatCurrency(leadMaintenanceSavings)}/yr
                        </div>
                      </div>
                    );
                  }
                  if (leadHoursSaved > 0) {
                    return (
                      <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 24 }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                          Time Saved
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 500, color: '#60a5fa', fontFamily: FONTS.headline }}>
                          {leadHoursSaved} hrs/mo
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Lead Form */}
              <form onSubmit={handleLeadFormSubmit} style={{ textAlign: 'left', maxWidth: 400, margin: '0 auto' }}>
                {/* Hidden fields for attribution and calculator data */}
                {(() => {
                  const attribution = getAttribution();
                  return (
                    <>
                      <input type="hidden" name="gclid" value={attribution.gclid || ''} />
                      <input type="hidden" name="utm_source" value={attribution.utm_source || ''} />
                      <input type="hidden" name="utm_medium" value={attribution.utm_medium || ''} />
                      <input type="hidden" name="utm_campaign" value={attribution.utm_campaign || ''} />
                      <input type="hidden" name="utm_content" value={attribution.utm_content || ''} />
                      <input type="hidden" name="utm_term" value={attribution.utm_term || ''} />
                      <input type="hidden" name="platform" value={attribution.platform || 'direct'} />
                      <input type="hidden" name="quote_value" value={calculations.project.azaiTotal} />
                      <input type="hidden" name="selected_platform" value={currentCMS} />
                      <input type="hidden" name="company_size" value={companySize} />
                      <input type="hidden" name="industry" value={industry} />
                      <input type="hidden" name="budget_range" value={budgetRange} />
                    </>
                  );
                })()}
                <div style={{ display: 'grid', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6, fontFamily: FONTS.body }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      value={leadFormData.name}
                      onChange={(e) => handleLeadFormChange('name', e.target.value)}
                      onFocus={trackFormStart}
                      placeholder="John Smith"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.05)',
                        fontSize: 14,
                        fontFamily: FONTS.body,
                        color: COLORS.white,
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6, fontFamily: FONTS.body }}>
                      Work Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={leadFormData.email}
                      onChange={(e) => {
                        handleLeadFormChange('email', e.target.value);
                        const freedomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com'];
                        const domain = e.target.value.split('@')[1]?.toLowerCase();
                        setShowEmailNudge(domain && freedomains.includes(domain));
                      }}
                      onFocus={trackFormStart}
                      placeholder="john@company.com"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.05)',
                        fontSize: 14,
                        fontFamily: FONTS.body,
                        color: COLORS.white,
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                    {showEmailNudge && (
                      <div style={{
                        marginTop: 6,
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.45)',
                        fontFamily: FONTS.body,
                        lineHeight: 1.4,
                      }}>
                        For faster scheduling, please use your work email if available
                      </div>
                    )}
                  </div>
                  {/* SMB path: Company field only (full width) */}
                  {companySize === 'startup' ? (
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6, fontFamily: FONTS.body }}>
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={leadFormData.company}
                        onChange={(e) => handleLeadFormChange('company', e.target.value)}
                        onFocus={trackFormStart}
                        placeholder="Acme Inc"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.15)',
                          background: 'rgba(255,255,255,0.05)',
                          fontSize: 14,
                          fontFamily: FONTS.body,
                          color: COLORS.white,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ) : (
                    /* Enterprise/Mid-Market path: Company + Phone, Revenue, Notes, AOR checkbox */
                    <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6, fontFamily: FONTS.body }}>
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={leadFormData.company}
                        onChange={(e) => handleLeadFormChange('company', e.target.value)}
                        onFocus={trackFormStart}
                        placeholder="Acme Inc"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.15)',
                          background: 'rgba(255,255,255,0.05)',
                          fontSize: 14,
                          fontFamily: FONTS.body,
                          color: COLORS.white,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6, fontFamily: FONTS.body }}>
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={leadFormData.phone}
                        onChange={(e) => handleLeadFormChange('phone', e.target.value)}
                        onFocus={trackFormStart}
                        placeholder="(555) 123-4567"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.15)',
                          background: 'rgba(255,255,255,0.05)',
                          fontSize: 14,
                          fontFamily: FONTS.body,
                          color: COLORS.white,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                  {/* Annual Revenue - Qualification intel */}
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6, fontFamily: FONTS.body }}>
                      Annual Revenue
                    </label>
                    <select
                      name="annual_revenue"
                      value={leadFormData.annualRevenue}
                      onChange={(e) => handleLeadFormChange('annualRevenue', e.target.value)}
                      onFocus={trackFormStart}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.05)',
                        fontSize: 14,
                        fontFamily: FONTS.body,
                        color: leadFormData.annualRevenue ? COLORS.white : 'rgba(255,255,255,0.4)',
                        outline: 'none',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                      }}
                    >
                      <option value="" style={{ background: '#1a1a1a' }}>Select range</option>
                      <option value="under_5m" style={{ background: '#1a1a1a' }}>Under $5M</option>
                      <option value="5m_25m" style={{ background: '#1a1a1a' }}>$5M - $25M</option>
                      <option value="25m_100m" style={{ background: '#1a1a1a' }}>$25M - $100M</option>
                      <option value="100m_500m" style={{ background: '#1a1a1a' }}>$100M - $500M</option>
                      <option value="500m_plus" style={{ background: '#1a1a1a' }}>$500M+</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6, fontFamily: FONTS.body }}>
                      Anything else we should know?
                    </label>
                    <textarea
                      name="message"
                      value={leadFormData.notes}
                      onChange={(e) => handleLeadFormChange('notes', e.target.value)}
                      onFocus={trackFormStart}
                      placeholder="Timeline, specific requirements, questions..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.05)',
                        fontSize: 14,
                        fontFamily: FONTS.body,
                        color: COLORS.white,
                        outline: 'none',
                        resize: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        cursor: 'pointer',
                        padding: '12px 16px',
                        borderRadius: 8,
                        border: `1px solid ${leadFormData.interestedInAOR ? COLORS.orange : 'rgba(255,255,255,0.15)'}`,
                        background: leadFormData.interestedInAOR ? `${COLORS.orange}15` : 'rgba(255,255,255,0.03)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <input
                        type="checkbox"
                        name="interested_in_aor"
                        checked={leadFormData.interestedInAOR}
                        onChange={(e) => handleLeadFormChange('interestedInAOR', e.target.checked)}
                        style={{
                          width: 18,
                          height: 18,
                          marginTop: 2,
                          accentColor: COLORS.orange,
                          cursor: 'pointer',
                        }}
                      />
                      <span style={{
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.8)',
                        fontFamily: FONTS.body,
                        lineHeight: 1.5,
                      }}>
                        I'm also interested in ongoing support (Agency of Record) after launch
                      </span>
                    </label>
                  </div>
                    </>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmittingLead || !leadFormData.name.trim() || !leadFormData.email.trim()}
                  onClick={() => {
                    trackCtaClick('book_time_continue', { 
                      quoteValue: calculations.project.azaiTotal, 
                      platform: currentCMS 
                    });
                  }}
                  style={{
                    width: '100%',
                    marginTop: 24,
                    padding: '16px 24px',
                    background: isSubmittingLead ? 'rgba(240, 90, 40, 0.6)' : COLORS.orange,
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: isSubmittingLead ? 'not-allowed' : 'pointer',
                    fontFamily: FONTS.body,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {isSubmittingLead ? (
                    companySize === 'startup' ? 'Sending Toolkit...' : 'Opening Calendar...'
                  ) : (
                    <>
                      {companySize === 'startup' ? 'Get Free Toolkit' : 'Continue to Book a Time'}
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        {companySize === 'startup' ? (
                          <>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </>
                        ) : (
                          <>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </>
                        )}
                      </svg>
                    </>
                  )}
                </button>

                <p style={{
                  marginTop: 12,
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.4)',
                  textAlign: 'center',
                  fontFamily: FONTS.body,
                }}>
                  {companySize === 'startup'
                    ? 'Your information is secure. We\'ll send your toolkit and occasional migration tips — unsubscribe anytime.'
                    : 'Your information is secure and will only be used to prepare for our call.'
                  }
                </p>
                {/* Startup upsell after form */}
                {companySize === 'startup' && (
                  <p style={{
                    marginTop: 16,
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.35)',
                    textAlign: 'center',
                    fontFamily: FONTS.body,
                    lineHeight: 1.5,
                  }}>
                    When you're ready for enterprise-grade support, we're here.
                  </p>
                )}
              </form>

              {/* Guide Download Confirmation - shown after SMB form submission */}
              {showGuideConfirmation && (
                <div style={{
                  marginTop: 24,
                  padding: '24px',
                  background: 'rgba(74, 222, 128, 0.1)',
                  border: '1px solid rgba(74, 222, 128, 0.3)',
                  borderRadius: 12,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
                  <h4 style={{
                    fontSize: TYPE.h3.size,
                    fontWeight: 500,
                    color: '#4ade80',
                    fontFamily: FONTS.headline,
                    marginBottom: 8,
                  }}>
                    Your Migration Toolkit is On Its Way!
                  </h4>
                  <p style={{
                    fontSize: TYPE.body.size,
                    color: 'rgba(255,255,255,0.6)',
                    fontFamily: FONTS.body,
                    lineHeight: 1.6,
                    marginBottom: 16,
                  }}>
                    Check your inbox — we've sent your free migration guide, Webflow templates, and resource library.
                  </p>
                  <p style={{
                    fontSize: TYPE.small.size,
                    color: 'rgba(255,255,255,0.35)',
                    fontFamily: FONTS.body,
                    lineHeight: 1.5,
                  }}>
                    When you're ready for enterprise-grade migration support,{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setShowGuideConfirmation(false);
                        setCompanySize('enterprise');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.orange,
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontFamily: FONTS.body,
                        fontSize: TYPE.small.size,
                        padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      upgrade to an enterprise consultation
                    </button>.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Assumptions footnote */}
          <div style={{
            marginTop: '3rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            fontSize: 12,
            color: 'rgba(255, 255, 255, 0.4)',
            lineHeight: 1.7,
            fontFamily: FONTS.body,
          }}>
            <strong style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Assumptions:</strong> Azai Studios rate $250/hr. Competitor agency rates ~$175/hr. SEO specialist rate $200/hr. Licensing based on typical enterprise tiers.
            Webflow hosting included in enterprise plans. Page estimates include design (2.5h) and development (3h) per page.
            Actual costs may vary based on specific requirements, integrations, and negotiated rates.
          </div>
        </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Sticky CTA Bar - hidden when form is open */}
      <div
        className={`sticky-cta ${isInDarkSection ? 'sticky-cta--light' : ''} ${isCtaFormOpen ? 'sticky-cta--hidden' : ''}`}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flex: 1,
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            <span style={{
              fontSize: 11,
              color: isInDarkSection ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: FONTS.body,
            }}>
              Your Quote
            </span>
            <span style={{
              fontSize: 20,
              fontWeight: 500,
              color: isInDarkSection ? COLORS.black : COLORS.white,
              fontFamily: FONTS.headline,
            }}>
              {formatCurrency(calculations.project.azaiTotal)}
            </span>
          </div>
          {(() => {
            // Calculate platform-only savings for floating CTA
            const cmsPlatformOnly = calculations.currentCms.annualLicensing + calculations.currentCms.annualHosting;
            const webflowPlatformOnly = calculations.webflowCms.annualLicensing;
            const platformOnlySavings = cmsPlatformOnly - webflowPlatformOnly;
            
            // Use toggle to determine which savings to show
            const displaySavings = includeMaintenance 
              ? calculations.migration.annualPlatformSavings 
              : platformOnlySavings;
            
            if (displaySavings > 0) {
              return (
                <div 
                  className="sticky-cta__savings"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    background: isInDarkSection ? 'rgba(22, 163, 74, 0.1)' : 'rgba(74, 222, 128, 0.15)',
                    borderRadius: 20,
                    flexShrink: 0,
                  }}>
                  <svg className="sticky-cta__savings-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isInDarkSection ? '#16a34a' : '#4ade80'} strokeWidth="2.5">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                    <polyline points="17 6 23 6 23 12"></polyline>
                  </svg>
                  <span style={{
                    fontSize: 13,
                    color: isInDarkSection ? '#16a34a' : '#4ade80',
                    fontWeight: 500,
                    fontFamily: FONTS.body,
                    whiteSpace: 'nowrap',
                  }}>
                    Save {formatCurrency(displaySavings)}/yr
                  </span>
                </div>
              );
            }
            // When Webflow costs more: show net savings if maintenance offsets it, or hours saved
            const ctaMaintenanceSavings = calculations.currentCms.annualMaintenance - calculations.webflowCms.annualMaintenance;
            const ctaNetSavings = displaySavings + ctaMaintenanceSavings;
            const ctaHoursSaved = calculations.currentCms.maintenanceHoursPerMonth - calculations.webflowCms.maintenanceHoursPerMonth;
            if (includeMaintenance && ctaNetSavings > 0) {
              // Maintenance savings more than offset the platform cost — show net
              return null; // displaySavings already accounts for maintenance when toggle is on
            }
            if (ctaHoursSaved > 0) {
              return (
                <div 
                  className="sticky-cta__savings"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    background: isInDarkSection ? 'rgba(37, 99, 235, 0.1)' : 'rgba(96, 165, 250, 0.15)',
                    borderRadius: 20,
                    flexShrink: 0,
                  }}>
                  <svg className="sticky-cta__savings-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isInDarkSection ? '#2563eb' : '#60a5fa'} strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span style={{
                    fontSize: 13,
                    color: isInDarkSection ? '#2563eb' : '#60a5fa',
                    fontWeight: 500,
                    fontFamily: FONTS.body,
                    whiteSpace: 'nowrap',
                  }}>
                    Save {ctaHoursSaved} hrs/mo
                  </span>
                </div>
              );
            }
            return null;
          })()}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => {
              trackCtaClick('book_call', { 
                quoteValue: calculations.project.azaiTotal, 
                platform: currentCMS 
              });
              handleBookNowClick();
            }}
            className={`sticky-cta__primary ${isInDarkSection ? 'sticky-cta__primary--dark' : ''}`}
            style={{ border: 'none', cursor: 'pointer' }}
          >
            {companySize === 'startup' ? 'Get Free Guide' : companySize === 'midmarket' ? 'Book Strategy Call' : 'Schedule Executive Briefing'}
          </button>
        </div>
      </div>
    </div>
  );
}
